import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { useDispatch, useSelector } from "react-redux";
import API from "../../../services";
import { AppDispatch, RootState } from "../../../store/store";
import { getRefId } from "../../../store/authActions";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import GifBoxRoundedIcon from "@mui/icons-material/GifBoxRounded";
import CurrencyBitcoinRoundedIcon from "@mui/icons-material/CurrencyBitcoinRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CancelIcon from "@mui/icons-material/Cancel";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import QRCode from "react-qr-code";
import { getWhitelabelItemStatusRaw, normalizeWlStatus } from "./whitelabelUi";
import { SkinPickerCarousel } from "./SkinPickerCarousel";
import { WhitelabelPackagePicker, parseWhitelabelPackagePaymentMethods } from "./WhitelabelPackagePicker";
import WhitelabelSupportChatPopup from "./WhitelabelSupportChatPopup";
import OCR_API from "../../../services/ocr";
import SupportIcon from "../../../assets/images/support.svg";
import DefaultWlFavicon from "../../../assets/images/whitelabel-default-favicon.png";
type StepKey = "branding" | "setup" | "packages" | "review";
type WlStatus = "IN_PROGRESS" | "SUCCESS" | "FAILED" | "";

/** Optional palette shape on skin items from API; whitelabel create/update uses `themeId` only. */
type WlThemeColors = {
  primaryColor: string;
  secondaryColor: string;
  headerColor: string;
  backgroundColor: string;
};

type WlThemeCard = {
  themeId?: string;
  name: string;
  primary: string;
  secondary: string;
  background: string;
  imageUrls: [string, string, string];
};

type WlThemeCategoryConfig = {
  category: string;
  themes: WlThemeCard[];
};

interface SkinItem {
  skinId: string;
  name: string;
  logo: string;
  categories?: CategoryItem[];
  previewImages: Array<string | { url: string; name?: string }>;
  previews?: Array<{ url: string; name: string }>;
  siteUrl: string;
  tglink?: string;
  weblink?: string;
  /** Skins API does not supply whitelabel theme colors; kept optional for typing only. */
  themeSeed?: WlThemeColors | null;
}

interface CategoryItem {
  key: string;
  label: string;
}

/** Quote line item returned from POST /whitelabel/packages. */
interface WhitelabelPackage {
  packageId: string;
  name?: string;
  pointsCredited: number;
  payAmount: number;
  discountAmount?: number;
  originalPayAmount?: number;
  currency?: string;
  packageDiscountPercent?: number;
  paymentMethods?: string[];
}

type TransactionTypeKey = "FIAT" | "CRYPTO";

type PostSubmitPaymentStep = "qr";
type PaymentOptionKey = "upiBank" | "crypto" | "offline";
type PaymentTransferTab = "upi" | "bankTransfer";
type PaymentFlowKind = "fiat" | "crypto";

type WlCryptoCurrencyItem = {
  cryptoCurrency: string;
  blockChain: string;
  networkId: string;
  tokenType?: string;
  tokenAddress?: string;
  conversions?: {
    deposit_exchange_rate?: number;
    withdrawal_exchange_rate?: number;
    exchange_currency?: string;
  };
};

type PaymentBankDetails = {
  accountNumber: string;
  accountName: string;
  ifsc: string;
  bankId: string;
  bankName: string;
};

type MissingMandatoryItem = { id: string; label: string; step: StepKey };

const steps: StepKey[] = ["branding", "setup", "packages", "review"];

const STEPPER_STEPS: { key: StepKey; label: string }[] = [
  { key: "branding", label: "Brand" },
  { key: "setup", label: "Basics" },
  { key: "packages", label: "Packages" },
  { key: "review", label: "Confirm" },
];

/** Primary / secondary actions — Continue & Submit use primary. */
const BTN_PRIMARY =
  "font-semibold shadow-[0_2px_12px_color-mix(in_srgb,var(--border-color)_25%,transparent)] transition-[opacity,transform] hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:opacity-45 disabled:cursor-not-allowed";
const BTN_PRIMARY_CLASS = `p-2.5 text-sm text-white rounded-lg border-2 border-[color-mix(in_srgb,var(--border-color)_50%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_28%,#0c1222)] ${BTN_PRIMARY}`;
const BTN_SECONDARY_CLASS =
  "p-2.5 text-sm text-zinc-300 rounded-lg border border-zinc-600/90 bg-zinc-800/60 transition-colors hover:bg-zinc-700/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

/** Matches `variables.css` / theme `--border-color` */
const STEPPER_PRIMARY = "var(--border-color)";
const STEPPER_LINE_GRAY = "var(--wl-stepper-line-gray)";
const STEPPER_INACTIVE_FILL = "var(--wl-stepper-inactive-fill)";
const STEPPER_INACTIVE_BORDER = "var(--wl-stepper-inactive-border)";

const wlStepperIcon = (stepKey: StepKey) => {
  const sx = { fontSize: 22, color: "#FFFFFF" };
  switch (stepKey) {
    case "setup":
      return <LockRoundedIcon sx={sx} />;
    case "branding":
      return <PersonRoundedIcon sx={sx} />;
    case "packages":
      return <AccountBalanceWalletRoundedIcon sx={sx} />;
    case "review":
      return <CheckRoundedIcon sx={sx} />;
    default:
      return <LockRoundedIcon sx={sx} />;
  }
};

/** Debounce for whitelabel name / subdomain availability API checks while typing. */
/** Pause typing before hitting availability APIs (per field, independent). */
const WL_NAME_DOMAIN_CHECK_DEBOUNCE_MS = 800;
/** Minimum length for brand name and subdomain before checks / continue. */
const WL_MIN_BRAND_SUB_CHARS = 3;

/** Default favicon used when the user does not upload one. */
const DEFAULT_WL_FAVICON_PATH = DefaultWlFavicon;

async function fetchPublicImageAsBase64(
  path: string
): Promise<{ base64: string; mime: string }> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to load ${path}`);
  const blob = await res.blob();
  const mime = blob.type || "image/png";
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
  return { base64, mime };
}

/** Strip non-alphanumeric characters (used for brand validation and subdomain sanitization for API). */
const sanitizeAlphaNumeric = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "");
const stripDataUrlPrefix = (value: string) => value.replace(/^data:[^;]+;base64,/i, "").trim();

/** Normalize draft payload shape from API. */
function buildMergedWhitelabelDraftFormPayload(draft: unknown): Record<string, unknown> | null {
  if (!draft || typeof draft !== "object" || Array.isArray(draft)) return null;
  return { ...(draft as Record<string, unknown>) };
}

/** Fetch an image URL (absolute, relative, or cross-origin when CORS allows) as raw base64 + mime. */
async function fetchRemoteImageAsBase64(url: string): Promise<{ base64: string; mime: string }> {
  let useCredentials: RequestCredentials = "omit";
  if (typeof window !== "undefined") {
    if (url.startsWith("/")) {
      useCredentials = "same-origin";
    } else {
      try {
        if (new URL(url, window.location.href).origin === window.location.origin) {
          useCredentials = "same-origin";
        }
      } catch {
        /* ignore */
      }
    }
  }
  const res = await fetch(url, { credentials: useCredentials, mode: "cors" });
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const mime =
    blob.type && blob.type !== "application/octet-stream" ? blob.type : "image/png";
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
  return { base64, mime };
}

type DraftImageParse =
  | { kind: "empty" }
  | { kind: "inline"; base64: string; mime: string; preview: string }
  | { kind: "remote"; url: string; mimeFallback: string };

function parseDraftLogoOrLoaderInput(urlOrB64: string, explicitMime: string): DraftImageParse {
  const raw = String(urlOrB64 ?? "").trim();
  if (!raw) return { kind: "empty" };
  const mimeHint = String(explicitMime ?? "").trim();
  if (/^https?:\/\//i.test(raw) || raw.startsWith("/")) {
    return { kind: "remote", url: raw, mimeFallback: mimeHint };
  }
  if (/^data:/i.test(raw)) {
    const compact = raw.replace(/\s/g, "");
    const m = /^data:([^;]+);base64,(.+)$/i.exec(compact);
    if (m) {
      const mime = m[1].trim();
      const base64 = m[2].trim();
      return { kind: "inline", base64, mime, preview: `data:${mime};base64,${base64}` };
    }
  }
  const maybeB64 = stripDataUrlPrefix(raw).replace(/\s/g, "");
  if (maybeB64.length >= 32 && /^[A-Za-z0-9+/=]+$/.test(maybeB64)) {
    const mime = mimeHint || "image/png";
    return {
      kind: "inline",
      base64: maybeB64,
      mime,
      preview: `data:${mime};base64,${maybeB64}`,
    };
  }
  return { kind: "empty" };
}

type DraftBrandMediaSetters = {
  setLogo: (next: File | null) => void;
  setGeneratedLogoBase64: (next: string) => void;
  setGeneratedLogoMime: (next: string) => void;
  setGeneratedLogoPreviewUrl: (next: string) => void;
  setGif: (next: File | null) => void;
  setDraftLoaderBase64: (next: string) => void;
  setDraftLoaderMime: (next: string) => void;
  setDraftLoaderPreviewUrl: (next: string) => void;
};

async function hydrateSubmittedBrandAssets(
  submittedData: Record<string, unknown>,
  s: DraftBrandMediaSetters
) {
  const pick = (key: string) => String((submittedData as Record<string, unknown>)?.[key] ?? "").trim();

  s.setLogo(null);
  s.setGif(null);

  /** API may send `logo` / `loader` as base64 or data URL alongside or instead of `logoUrl` / `loaderUrl`. */
  const logoUrl =
    pick("logoUrl") ||
    String((submittedData as Record<string, unknown>)?.logo ?? "").trim();
  const logoType = pick("logoType");
  const logoParsed = parseDraftLogoOrLoaderInput(logoUrl, logoType || "image/png");
  if (logoParsed.kind === "empty") {
    s.setGeneratedLogoBase64("");
    s.setGeneratedLogoMime("");
    s.setGeneratedLogoPreviewUrl("");
  } else if (logoParsed.kind === "inline") {
    s.setGeneratedLogoBase64(logoParsed.base64);
    s.setGeneratedLogoMime(logoParsed.mime);
    s.setGeneratedLogoPreviewUrl(logoParsed.preview);
  } else {
    s.setGeneratedLogoBase64("");
    s.setGeneratedLogoMime(logoParsed.mimeFallback || "image/png");
    s.setGeneratedLogoPreviewUrl(logoParsed.url);
    try {
      const fetched = await fetchRemoteImageAsBase64(logoParsed.url);
      s.setGeneratedLogoBase64(fetched.base64);
      s.setGeneratedLogoMime(fetched.mime || logoParsed.mimeFallback || "image/png");
    } catch {
      /* keep URL preview; submit will try again */
    }
  }

  const loaderUrl =
    pick("loaderUrl") ||
    String((submittedData as Record<string, unknown>)?.loader ?? "").trim();
  const loaderType = pick("loaderType");
  const loaderParsed = parseDraftLogoOrLoaderInput(loaderUrl, loaderType || "image/gif");
  if (loaderParsed.kind === "empty") {
    s.setDraftLoaderBase64("");
    s.setDraftLoaderMime("");
    s.setDraftLoaderPreviewUrl("");
  } else if (loaderParsed.kind === "inline") {
    s.setDraftLoaderBase64(loaderParsed.base64);
    s.setDraftLoaderMime(loaderParsed.mime);
    s.setDraftLoaderPreviewUrl(loaderParsed.preview);
  } else {
    s.setDraftLoaderBase64("");
    s.setDraftLoaderMime(loaderParsed.mimeFallback || "image/gif");
    s.setDraftLoaderPreviewUrl(loaderParsed.url);
    try {
      const fetched = await fetchRemoteImageAsBase64(loaderParsed.url);
      s.setDraftLoaderBase64(fetched.base64);
      s.setDraftLoaderMime(fetched.mime || loaderParsed.mimeFallback || "image/gif");
    } catch {
      /* preview URL only */
    }
  }
}

const normalizeDomainSuffix = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .replace(/^\.+/, "")
    .replace(/\.+$/, "");

const pickSkinSiteUrl = (raw: unknown): string => {
  if (!raw || typeof raw !== "object") return "";
  const o = raw as Record<string, unknown>;
  return String(o.siteUrl ?? "").trim();
};

const normalizeCategoryList = (raw: unknown): CategoryItem[] => {
  if (!Array.isArray(raw)) return [];
  const list = raw
    .map((item: unknown): CategoryItem | null => {
      if (item && typeof item === "object") {
        const obj = item as Record<string, unknown>;
        const key = String(
          obj.key ??
            obj.id ??
            obj.categoryId ??
            obj.value ??
            obj.slug ??
            ""
        ).trim();
        const label = String(obj.label ?? obj.name ?? obj.title ?? key).trim();
        if (!key) return null;
        return { key, label: label || key };
      }
      const value = String(item ?? "").trim();
      if (!value) return null;
      return { key: value, label: value };
    })
    .filter((item): item is CategoryItem => Boolean(item));
  const seen = new Set<string>();
  return list.filter((item) => {
    const dedupeKey = item.key.toLowerCase();
    if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });
};

function normalizeHexColorOrEmpty(input: unknown): string {
  if (input == null) return "";
  let s = String(input).trim();
  if (!s) return "";
  if (!s.startsWith("#")) s = `#${s}`;
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    s = `#${r}${r}${g}${g}${b}${b}`;
  }
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  return "";
}

function normalizeThemeImageUrls(input: unknown): [string, string, string] {
  const list = Array.isArray(input)
    ? input
        .map((url) => String(url ?? "").trim())
        .filter((url) => url.length > 0)
    : [];
  return [list[0] ?? "", list[1] ?? "", list[2] ?? ""];
}

function normalizeThemeConfigs(raw: unknown): WlThemeCategoryConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): WlThemeCategoryConfig | null => {
      if (!entry || typeof entry !== "object") return null;
      const obj = entry as Record<string, unknown>;
      const category = String(obj.category ?? "").trim();
      if (!category) return null;
      const themeList = Array.isArray(obj.themes) ? obj.themes : [];
      const themes = themeList
        .map((theme): WlThemeCard | null => {
          if (!theme || typeof theme !== "object") return null;
          const t = theme as Record<string, unknown>;
          const themeId = String(t.themeId ?? "").trim();
          const name = String(t.name ?? "").trim();
          if (!name) return null;
          return {
            themeId: themeId || undefined,
            name,
            primary: normalizeHexColorOrEmpty(t.primary),
            secondary: normalizeHexColorOrEmpty(t.secondary),
            background: normalizeHexColorOrEmpty(t.background),
            imageUrls: normalizeThemeImageUrls(t.imageUrls),
          };
        })
        .filter((theme): theme is WlThemeCard => Boolean(theme));
      if (themes.length === 0) return null;
      return { category, themes };
    })
    .filter((cfg): cfg is WlThemeCategoryConfig => Boolean(cfg));
}

function findThemeSelectionById(
  configs: WlThemeCategoryConfig[],
  themeIdRaw: string,
): { category: string; theme: WlThemeCard } | null {
  const wanted = String(themeIdRaw ?? "").trim();
  if (!wanted) return null;
  const wantedLower = wanted.toLowerCase();
  for (const cfg of configs) {
    for (const theme of cfg.themes) {
      const themeId = String(theme.themeId ?? "").trim();
      if (!themeId) continue;
      if (themeId === wanted || themeId.toLowerCase() === wantedLower) {
        return { category: cfg.category, theme };
      }
    }
  }
  return null;
}

function readSubmittedThemeId(submittedData: any): string {
  return String(submittedData?.themeId ?? "").trim();
}

function hexToRgbParts(hex: string): [number, number, number] {
  let normalized = normalizeHexColorOrEmpty(hex);
  if (!/^#[0-9a-f]{6}$/i.test(normalized)) normalized = "#000000";
  const raw = normalized.slice(1);
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  return [r, g, b];
}

const apiPost = (path: string, payload: any) => API.post(path, payload);
const apiCheckWhitelabelName = (
  payload: { whitelabelName: string; whitelabelId: string },
  config?: AxiosRequestConfig
) => {
  return API.post("/whitelabel/check-name", payload, config);
};
const apiCheckWhitelabelSubdomain = (
  payload: { subdomain: string; whitelabelId: string },
  config?: AxiosRequestConfig
) => {
  return API.post("/whitelabel/check-subdomain", payload, config);
};
/**
 * One skins/categories request for the whole app session.
 * Successful responses stay cached until `invalidateWlSkinsCategoriesRequestCache` (retry / payment-prefill),
 * so remounts and effect re-runs never fire a second HTTP for the same payload.
 */
let wlSkinsCategoriesRequest: Promise<AxiosResponse> | null = null;

/** Call from "Retry" (or flows that must refetch) so the next load hits the network. */
const invalidateWlSkinsCategoriesRequestCache = () => {
  wlSkinsCategoriesRequest = null;
};

const apiGetSkinsAndCategories = () => {
  if (wlSkinsCategoriesRequest) {
    return wlSkinsCategoriesRequest;
  }
  wlSkinsCategoriesRequest = API.post("/whitelabel/skins-and-categories", {}).catch((err) => {
    wlSkinsCategoriesRequest = null;
    throw err;
  });
  return wlSkinsCategoriesRequest;
};
const apiUpdateWhitelabelPaymentMethod = (payload: {
  whitelabelId: string;
  paymentMethod: string;
}) => {
  return API.post("/whitelabel/update-payment-method", payload);
};
const apiGetWhitelabelPackages = (payload: {
  categories: string[];
  enableCryptoTransactions: boolean;
  enableFiatTransactions: boolean;
  managePayments: boolean;
}) => {
  return API.post("/whitelabel/packages", payload);
};
const apiGetWhitelabelDraft = (payload: { userKey: string; whitelabelId: string }) => {
  return API.post("/whitelabel/draft", payload);
};
const WL_GENERATE_LOGO_TIMEOUT_MS = 75_000;
const WL_CREATE_UPDATE_TIMEOUT_MS = 75_000;
const MAX_GENERATED_LOGO_ATTEMPTS = 3;
const MAX_LOGO_UPLOAD_BYTES = 1024 * 1024; // 1MB
const MAX_GIF_UPLOAD_BYTES = 500 * 1024; // 500KB

const apiGenerateWhitelabelLogo = (payload: { whitelabelName: string }) => {
  return API.post("/whitelabel/generate-logo", payload, {
    timeout: WL_GENERATE_LOGO_TIMEOUT_MS,
  });
};
/** POST `/whitelabel/create` body. */
type WhitelabelCreateUpdatePayload = {
  categories: string[];
  enableCryptoTransactions: boolean;
  enableFiatTransactions: boolean;
  favicon: string;
  faviconType: string;
  loader: string;
  loaderType: string;
  logo: string;
  logoType: string;
  managePayments: boolean;
  packageId: string;
  themeId?: string;
  skinId: string;
  subdomain: string;
  whitelabelName: string;
};

/** Same as create body plus `whitelabelId` (POST `/whitelabel/update`). */
type WhitelabelUpdatePayload = WhitelabelCreateUpdatePayload & {
  whitelabelId: string;
};

const apiCreateWhitelabel = (payload: WhitelabelCreateUpdatePayload) => {
  return API.post("/whitelabel/create", payload, {
    timeout: WL_CREATE_UPDATE_TIMEOUT_MS,
  });
};
const apiUpdateWhitelabel = (payload: WhitelabelUpdatePayload) => {
  return API.post("/whitelabel/update", payload, {
    timeout: WL_CREATE_UPDATE_TIMEOUT_MS,
  });
};
const apiInitiateDeposit = (payload: { whitelabelId: string; packageId: string }) => {
  const whitelabelId = String(payload.whitelabelId || "").trim();
  const packageId = String(payload.packageId || "").trim();
  return API.post("/whitelabel/initiate-deposit", {
    whitelabelId,
    packageId,
  });
};
const apiGetWlCryptoCurrencies = () => {
  return API.post("/whitelabel/get-wl-crypto-currencies", "");
};
const apiGetWlWalletAddress = (payload: {
  currency: string;
  networkId: string;
  packageId: string;
  whitelabelId: string;
}) => {
  return API.post("/whitelabel/get-wl-wallet-address", payload);
};
const apiProcessDeposit = (payload: {
  bankId: string;
  depositType: string;
  imageUrl: string;
  refId: string;
  tgProviderId: string;
  uKey: string;
  utr: string;
}) => {
  return API.post("/whitelabel/process-deposit", payload);
};

const mapRawToWhitelabelPackage = (raw: any): WhitelabelPackage | null => {
  if (!raw || typeof raw !== "object") return null;
  const packageId = String(raw.packageId ?? "").trim();
  if (!packageId) return null;
  return {
    packageId,
    name: raw.name,
    pointsCredited: Number(raw.pointsCredited ?? raw.points ?? 0),
    payAmount: Number(raw.payAmount ?? 0),
    discountAmount: (() => {
      const v = raw.discountAmount;
      if (v == null || v === "") return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    })(),
    originalPayAmount: (() => {
      const v = raw.originalPayAmount;
      if (v == null || v === "") return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    })(),
    currency: raw.currency ?? raw.currencyCode,
    packageDiscountPercent: (() => {
      const v = raw.packageDiscountPercent;
      if (v == null || v === "") return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    })(),
    paymentMethods: parseWhitelabelPackagePaymentMethods(raw),
  };
};

function packagePaymentMethodCodesInclude(methods: string[] | undefined, code: string): boolean {
  if (!methods?.length) return false;
  const want = String(code ?? "").trim().toUpperCase();
  if (!want) return false;
  return methods.some((m) => String(m ?? "").trim().toUpperCase() === want);
}

const normSkinLabel = (s: string) =>
  String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * Draft / submitted `skinId` vs skins-and-categories list `skinId`: exact match (trimmed string),
 * then case-insensitive id, then exact display name; else first skin in the list.
 */
const resolveSelectedSkinIdForList = (
  skins: readonly { skinId: string; name: string }[],
  draftSkinIdOrHint: string
): string => {
  if (!skins.length) return "";
  const want = String(draftSkinIdOrHint ?? "").trim();
  if (want) {
    const normId = (id: unknown) => String(id ?? "").trim();
    const hitExact = skins.find((s) => normId(s.skinId) === want);
    if (hitExact) return normId(hitExact.skinId);
    const lower = want.toLowerCase();
    const hitCi = skins.find((s) => normId(s.skinId).toLowerCase() === lower);
    if (hitCi) return normId(hitCi.skinId);
    const wantNorm = normSkinLabel(want);
    if (wantNorm) {
      const byName = skins.find((s) => normSkinLabel(s.name) === wantNorm);
      if (byName) return normId(byName.skinId);
    }
  }
  return String(skins[0].skinId ?? "").trim();
};

const CreateWhitelabel = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const browser = useSelector((state: RootState) => state.userDetails.Browser);
  const userDetails = useSelector((state: RootState) => state.userDetails.userDetails);

  const isTelegram = browser === "TG";
  const hasContact = Boolean(userDetails?.MobileNumber);

  const [loading, setLoading] = useState(true);
  /** When set, skins/categories failed to load or came back empty — user must retry. */
  const [setupError, setSetupError] = useState<string | null>(null);
  const [snapshotStatus, setSnapshotStatus] = useState<WlStatus>("");
  const [snapshotBalance, setSnapshotBalance] = useState<number | null>(null);
  const [supportTicketId, setSupportTicketId] = useState("");
  const [supportChatOpen, setSupportChatOpen] = useState(false);

  const [stepIdx, setStepIdx] = useState(0);
  /** 1 = navigating to a higher index, -1 = lower (drives step panel enter animation). */
  const stepTransitionDirRef = useRef<1 | -1>(1);
  /** Previous step index after layout; null only before first commit (skip initial animation). */
  const prevStepForAnimRef = useRef<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameChecking, setNameChecking] = useState(false);

  const [skins, setSkins] = useState<SkinItem[]>([]);
  /** Latest skins for `applySubmittedData` (runs before/after load without listing `skins` in deps). */
  const skinsRef = useRef<SkinItem[]>([]);
  skinsRef.current = skins;
  const [categories, setCategories] = useState<CategoryItem[]>([]);

  const [selectedSkinId, setSelectedSkinId] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryTouched, setCategoryTouched] = useState(false);

  const [themeConfigs, setThemeConfigs] = useState<WlThemeCategoryConfig[]>([]);
  const themeConfigsRef = useRef<WlThemeCategoryConfig[]>([]);
  themeConfigsRef.current = themeConfigs;
  const pendingThemeIdRef = useRef("");
  const [selectedThemeCategory, setSelectedThemeCategory] = useState("");
  const [selectedThemeName, setSelectedThemeName] = useState("");
  const [activeThemeBrowseCategory, setActiveThemeBrowseCategory] = useState("");
  const [themePreviewModal, setThemePreviewModal] = useState<{
    open: boolean;
    themeName: string;
    images: string[];
    index: number;
  }>({ open: false, themeName: "", images: [], index: 0 });

  const [whitelabelName, setWhitelabelName] = useState("");
  const [nameHasInvalidChars, setNameHasInvalidChars] = useState(false);
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const [nameAvailabilityError, setNameAvailabilityError] = useState("");
  const [domainUrlAvailable, setDomainUrlAvailable] = useState<boolean | null>(null);
  const [domainAvailabilityError, setDomainAvailabilityError] = useState("");
  const [domainChecking, setDomainChecking] = useState(false);
  const [subdomain, setSubdomain] = useState("");
  const [availableDomainSuffixes, setAvailableDomainSuffixes] = useState<string[]>([]);
  const [selectedDomainSuffix, setSelectedDomainSuffix] = useState("whitelabel.com");
  const [subdomainHasInvalidChars, setSubdomainHasInvalidChars] = useState(false);
  const [isSubdomainManuallyEdited, setIsSubdomainManuallyEdited] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [gif, setGif] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [gifPreviewUrl, setGifPreviewUrl] = useState("");
  /** Loader from draft (`loaderUrl` / base64) when not using a freshly uploaded GIF file. */
  const [draftLoaderBase64, setDraftLoaderBase64] = useState("");
  const [draftLoaderMime, setDraftLoaderMime] = useState("");
  const [draftLoaderPreviewUrl, setDraftLoaderPreviewUrl] = useState("");
  const [generatedLogoBase64, setGeneratedLogoBase64] = useState("");
  const [generatedLogoMime, setGeneratedLogoMime] = useState("");
  const [generatedLogoPreviewUrl, setGeneratedLogoPreviewUrl] = useState("");
  const [isGeneratingLogo, setIsGeneratingLogo] = useState(false);
  const [generateLogoAttemptCount, setGenerateLogoAttemptCount] = useState(0);

  /** When Fiat is enabled: own vs platform banks. `managePayments` on API = own bank accounts selected. */
  const [fiatBankSource, setFiatBankSource] = useState<"own" | "provided" | null>("own");
  const [enableFiatTransactions, setEnableFiatTransactions] = useState(true);
  const [enableCryptoTransactions, setEnableCryptoTransactions] = useState(true);
  const txTypesRef = useRef({ fiat: true, crypto: true });
  txTypesRef.current = { fiat: enableFiatTransactions, crypto: enableCryptoTransactions };
  /** Per-field generation counters so name/subdomain responses never cross. */
  const nameAvailabilityReqIdRef = useRef(0);
  const subdomainAvailabilityReqIdRef = useRef(0);
  const nameAvailabilityAbortRef = useRef<AbortController | null>(null);
  const subdomainAvailabilityAbortRef = useRef<AbortController | null>(null);
  const availabilityWlIdRef = useRef("");
  const lastCheckedNameRef = useRef<string | null>(null);
  const lastCheckedSubRef = useRef<string | null>(null);
  const nameCheckTimerRef = useRef<number | null>(null);
  const subdomainCheckTimerRef = useRef<number | null>(null);
  const editInitialAvailabilitySeedRef = useRef("");

  const [webMobile, setWebMobile] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [otp, setOtp] = useState("");
  const [contactVerifiedByWebOtp, setContactVerifiedByWebOtp] = useState(false);
  const [availablePackages, setAvailablePackages] = useState<WhitelabelPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [paymentPackageSnapshot, setPaymentPackageSnapshot] = useState<WhitelabelPackage | null>(null);
  const [packagesRetryNonce, setPackagesRetryNonce] = useState(0);

  const [postSubmitPaymentStep, setPostSubmitPaymentStep] = useState<PostSubmitPaymentStep | null>(null);
  /** Seed from URL on first paint so edit mode does not bump `createdWhitelabelId` after mount (avoids re-running the init load effect). */
  const [createdWhitelabelId, setCreatedWhitelabelId] = useState(() => {
    const w = String(searchParams.get("whitelabelId") || "").trim();
    const edit = searchParams.get("edit") === "1";
    return edit && w ? w : "";
  });
  const [paymentRefId, setPaymentRefId] = useState("");
  const [paymentBankId, setPaymentBankId] = useState("");
  const [paymentDepositType, setPaymentDepositType] = useState("");
  const [paymentTgProviderId, setPaymentTgProviderId] = useState("");
  const [paymentImageUrl, setPaymentImageUrl] = useState("");
  const [paymentUpiId, setPaymentUpiId] = useState("");
  const [paymentUpiIntent, setPaymentUpiIntent] = useState("");
  const [paymentQrImages, setPaymentQrImages] = useState<string[]>([]);
  const [paymentBankDetails, setPaymentBankDetails] = useState<PaymentBankDetails | null>(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOptionKey>("crypto");
  const [isPaymentOptionLocked, setIsPaymentOptionLocked] = useState(false);
  const [paymentTransferTab, setPaymentTransferTab] = useState<PaymentTransferTab>("upi");
  const [paymentScreenshotFile, setPaymentScreenshotFile] = useState<File | null>(null);
  const [paymentScreenshotPreviewUrl, setPaymentScreenshotPreviewUrl] = useState("");
  const [paymentOcrLoading, setPaymentOcrLoading] = useState(false);
  /** UTR input is shown only after OCR finishes for the current screenshot. */
  const [paymentUtrFieldVisible, setPaymentUtrFieldVisible] = useState(false);
  const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
  const [paymentQrLoading, setPaymentQrLoading] = useState(false);
  const [paymentUtr, setPaymentUtr] = useState("");
  const [paymentUtrSubmitting, setPaymentUtrSubmitting] = useState(false);
  const [paymentOfflineSubmitting, setPaymentOfflineSubmitting] = useState(false);
  const [paymentFlowKind, setPaymentFlowKind] = useState<PaymentFlowKind>("fiat");
  const [wlCryptoCurrList, setWlCryptoCurrList] = useState<WlCryptoCurrencyItem[]>([]);
  const [wlCryptoCurrLoading, setWlCryptoCurrLoading] = useState(false);
  const [selectedWlCryptoCurrency, setSelectedWlCryptoCurrency] = useState<WlCryptoCurrencyItem | null>(null);
  const [wlCryptoWalletAddress, setWlCryptoWalletAddress] = useState("");
  const [wlCryptoWalletLoading, setWlCryptoWalletLoading] = useState(false);
  const paymentResumeInitializedRef = useRef(false);
  /** Avoid duplicate `/whitelabel/skins-and-categories` when init effect re-runs (e.g. UserKey hydrates). */
  const wlInitSkinsLoadedRef = useRef(false);
  /** Single in-flight `loadSetupData` per mount so overlapping init effect runs await one promise (one network call). */
  const wlSetupDataInFlightRef = useRef<Promise<void> | null>(null);
  /** Avoid duplicate `/whitelabel/draft` for the same user + whitelabel when `createdWhitelabelId` syncs with URL id. */
  const wlInitSnapshotSigRef = useRef<string | null>(null);
  const loadSnapshotForInitRef = useRef<() => Promise<void>>(async () => {});
  const mainScrollRef = useRef<HTMLDivElement | null>(null);
  const [footerHeightPx, setFooterHeightPx] = useState(88);
  const footerClearancePx = 14;
  const scrollBottomSafePx = footerHeightPx + footerClearancePx + 6;
  /** True during debounce window before availability API runs. */
  const [nameCheckQueued, setNameCheckQueued] = useState(false);
  const [domainCheckQueued, setDomainCheckQueued] = useState(false);

  const canUseStepper = isTelegram ? hasContact : hasContact || contactVerifiedByWebOtp;
  const resumePaymentRequested = searchParams.get("resumePayment") === "1";
  const resumeWhitelabelId = String(searchParams.get("whitelabelId") || "").trim();
  const forceEditRequested = searchParams.get("edit") === "1";
  const editWhitelabelId = String(searchParams.get("whitelabelId") || "").trim();
  const resumePackageId = String(searchParams.get("packageId") || "").trim();
  const stepperVisualIdx =
    stepIdx < 0 ? 0 : Math.min(Math.max(stepIdx, 0), STEPPER_STEPS.length - 1);
  const currentStep = steps[stepperVisualIdx];
  const readOnlySnapshot =
    !forceEditRequested && snapshotStatus === "SUCCESS";
  const failedSnapshot = snapshotStatus === "FAILED";
  const editPrefillData = useMemo(() => {
    const state = location.state as { prefillData?: Record<string, unknown> } | null;
    const prefill = state?.prefillData;
    return prefill && typeof prefill === "object" ? prefill : null;
  }, [location.state]);
  const paymentPrefillData = useMemo(() => {
    const state = location.state as { paymentPrefillData?: Record<string, unknown> } | null;
    const prefill = state?.paymentPrefillData;
    return prefill && typeof prefill === "object" ? prefill : null;
  }, [location.state]);
  const paymentReturnTo = useMemo(() => {
    const state = location.state as { paymentReturnTo?: unknown } | null;
    const raw = String(state?.paymentReturnTo ?? "").trim();
    return raw.startsWith("/") ? raw : "";
  }, [location.state]);
  /** Add funds / resume payment opened from an active brand page — offline checkout is hidden. */
  const isActiveWhitelabelPaymentFlow = useMemo(() => {
    const raw = paymentReturnTo.trim();
    if (!raw) return false;
    const path = raw.split("?")[0].split("#")[0];
    const query = raw.includes("?") ? raw.slice(raw.indexOf("?") + 1).split("#")[0] : "";
    const mode = new URLSearchParams(query).get("mode");
    return path.startsWith("/whitelabel/details/") && mode === "active";
  }, [paymentReturnTo]);
  const isPaymentPrefillFlow = Boolean(resumePaymentRequested && paymentPrefillData);
  const shouldFetchDraftOnInit = !(forceEditRequested && editPrefillData);

  const goToStep = useCallback((nextRaw: number) => {
    setStepIdx((prev) => {
      const next = Math.max(0, Math.min(nextRaw, steps.length - 1));
      if (next !== prev) stepTransitionDirRef.current = next > prev ? 1 : -1;
      return next;
    });
  }, []);

  const stepPanelAnimClass = useMemo(() => {
    const prev = prevStepForAnimRef.current;
    const shouldAnim = prev !== null && prev !== stepperVisualIdx;
    if (!shouldAnim) return "";
    return stepTransitionDirRef.current >= 0
      ? "wl-step-panel-anim-forward"
      : "wl-step-panel-anim-back";
  }, [stepperVisualIdx]);

  /** Sync "previous step" after the enter animation so quick re-renders do not drop the anim class. */
  useLayoutEffect(() => {
    const id = window.setTimeout(() => {
      prevStepForAnimRef.current = stepperVisualIdx;
    }, 210);
    return () => window.clearTimeout(id);
  }, [stepperVisualIdx]);

  /** Keep create-flow height aligned with actual fixed footer height to avoid bottom clipping. */
  useLayoutEffect(() => {
    const updateFooterHeight = () => {
      const footerEl = document.querySelector<HTMLElement>('[data-app-footer="true"]');
      const next = Math.ceil(footerEl?.getBoundingClientRect().height ?? 88);
      setFooterHeightPx((prev) => (prev === next ? prev : next));
    };

    updateFooterHeight();
    const footerEl = document.querySelector<HTMLElement>('[data-app-footer="true"]');
    const ro = footerEl ? new ResizeObserver(updateFooterHeight) : null;
    if (footerEl && ro) ro.observe(footerEl);
    window.addEventListener("resize", updateFooterHeight);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateFooterHeight);
    };
  }, []);

  const themeSelectionRequired = themeConfigs.length > 0;
  const hasSelectedTheme = !themeSelectionRequired || Boolean(selectedThemeCategory && selectedThemeName);
  const basicsSelectionComplete = useMemo(
    () => Boolean(selectedSkinId) && selectedCategories.length > 0 && hasSelectedTheme,
    [selectedSkinId, selectedCategories, hasSelectedTheme]
  );
  const brandingComplete = useMemo(() => {
    const nameOk = whitelabelName.trim().length >= WL_MIN_BRAND_SUB_CHARS;
    const subSan = sanitizeAlphaNumeric(subdomain).trim().toLowerCase();
    const subOk =
      subSan.length >= WL_MIN_BRAND_SUB_CHARS && !subdomainHasInvalidChars;
    return nameOk && subOk && nameAvailable === true && domainUrlAvailable === true;
  }, [
    whitelabelName,
    subdomain,
    subdomainHasInvalidChars,
    nameAvailable,
    domainUrlAvailable,
  ]);
  const paymentModesComplete = useMemo(() => {
    const txOk = enableFiatTransactions || enableCryptoTransactions;
    const fiatChoiceOk = !enableFiatTransactions || fiatBankSource !== null;
    return txOk && fiatChoiceOk;
  }, [enableFiatTransactions, enableCryptoTransactions, fiatBankSource]);
  const basicsComplete = useMemo(
    () => basicsSelectionComplete && paymentModesComplete,
    [basicsSelectionComplete, paymentModesComplete]
  );

  /** POST /whitelabel/packages payload aligned with backend contract. */
  const packageQuoteRequest = useMemo(() => {
    const managePayments = enableFiatTransactions ? fiatBankSource === "own" : false;
    return {
      categories: selectedCategories,
      managePayments,
      enableCryptoTransactions,
      enableFiatTransactions,
    };
  }, [
    selectedCategories,
    enableFiatTransactions,
    enableCryptoTransactions,
    fiatBankSource,
  ]);

  const packagesComplete = useMemo(() => {
    if (packagesLoading) return false;
    if (packagesError) return false;
    if (availablePackages.length === 0) return false;
    return Boolean(selectedPackageId);
  }, [packagesLoading, packagesError, availablePackages.length, selectedPackageId]);

  const packagesStepCanContinue = useMemo(() => packagesComplete, [packagesComplete]);

  const logoComplete = useMemo(() => {
    const pv = generatedLogoPreviewUrl.trim();
    return (
      Boolean(logo) ||
      generatedLogoBase64.trim().length > 0 ||
      /^https?:\/\//i.test(pv) ||
      /^data:/i.test(pv) ||
      (pv.startsWith("/") && pv.length > 1)
    );
  }, [logo, generatedLogoBase64, generatedLogoPreviewUrl]);

  const allMandatoryFilled = useMemo(
    () => basicsComplete && brandingComplete && logoComplete && packagesComplete,
    [basicsComplete, brandingComplete, logoComplete, packagesComplete]
  );

  const missingMandatoryItems = useMemo((): MissingMandatoryItem[] => {
    const missing: MissingMandatoryItem[] = [];
    if (!selectedSkinId) missing.push({ id: "skin", label: "Skin", step: "setup" });
    if (selectedCategories.length === 0)
      missing.push({ id: "categories", label: "Categories", step: "setup" });
    if (themeSelectionRequired && !hasSelectedTheme)
      missing.push({ id: "theme", label: "Choose a brand theme", step: "setup" });
    if (!whitelabelName.trim())
      missing.push({ id: "wl-name", label: "Brand name", step: "branding" });
    else if (whitelabelName.trim().length < WL_MIN_BRAND_SUB_CHARS)
      missing.push({
        id: "wl-name-len",
        label: `Brand name — at least ${WL_MIN_BRAND_SUB_CHARS} characters`,
        step: "branding",
      });
    else if (nameAvailable !== true) {
      if (nameAvailable === false)
        missing.push({
          id: "wl-name-bad",
          label: "Brand name is taken — pick a different one",
          step: "branding",
        });
      else
        missing.push({
          id: "wl-name-check",
          label: "We couldn't confirm your brand name — try again",
          step: "branding",
        });
    }
    const subSanMissing = sanitizeAlphaNumeric(subdomain).trim().toLowerCase();
    if (!subdomain.trim()) missing.push({ id: "sub", label: "Web address", step: "branding" });
    else if (subdomainHasInvalidChars)
      missing.push({
        id: "sub-chars",
        label: "Web address — lowercase letters and numbers only",
        step: "branding",
      });
    else if (subSanMissing.length < WL_MIN_BRAND_SUB_CHARS)
      missing.push({
        id: "sub-len",
        label: `Web address — at least ${WL_MIN_BRAND_SUB_CHARS} characters`,
        step: "branding",
      });
    else if (domainUrlAvailable !== true) {
      if (domainUrlAvailable === false)
        missing.push({
          id: "sub-bad",
          label: "Web address is taken — try another",
          step: "branding",
        });
      else
        missing.push({
          id: "sub-check",
          label: "We couldn't confirm your web address — try again",
          step: "branding",
        });
    }
    const hasLogoFromDraftOrUpload =
      Boolean(logo) ||
      generatedLogoBase64.trim().length > 0 ||
      /^https?:\/\//i.test(generatedLogoPreviewUrl.trim()) ||
      /^data:/i.test(generatedLogoPreviewUrl.trim()) ||
      (generatedLogoPreviewUrl.trim().startsWith("/") &&
        generatedLogoPreviewUrl.trim().length > 1);
    if (!hasLogoFromDraftOrUpload) {
      missing.push({
        id: "logo",
        label: "Logo is required - upload or generate one",
        step: "branding",
      });
    }
    if (!enableFiatTransactions && !enableCryptoTransactions) {
      missing.push({
        id: "tx",
        label: "Turn on crypto, fiat, or both under How players pay",
        step: "setup",
      });
    }
    if (enableFiatTransactions && fiatBankSource === null) {
      missing.push({
        id: "fiat-src",
        label: "Choose fiat setup: your banks or platform-provided details",
        step: "setup",
      });
    }
    if (packagesLoading)
      missing.push({ id: "pkg-load", label: "Plans are still loading", step: "packages" });
    else if (!packagesError && availablePackages.length === 0)
      missing.push({ id: "pkg-none", label: "No plan available", step: "packages" });
    else if (!selectedPackageId)
      missing.push({ id: "pkg-pick", label: "Pick a plan", step: "packages" });
    return missing;
  }, [
    selectedSkinId,
    selectedCategories.length,
    themeSelectionRequired,
    hasSelectedTheme,
    whitelabelName,
    subdomain,
    subdomainHasInvalidChars,
    nameAvailable,
    domainUrlAvailable,
    logo,
    generatedLogoBase64,
    generatedLogoPreviewUrl,
    enableFiatTransactions,
    enableCryptoTransactions,
    fiatBankSource,
    packagesLoading,
    packagesError,
    availablePackages.length,
    selectedPackageId,
  ]);

  useEffect(() => {
    if (isPaymentPrefillFlow) return;
    /** Quote on the Packages step only; Confirm reuses the list (avoids a redundant API on stepper click). */
    const bootstrapReviewPackages =
      currentStep === "review" && availablePackages.length === 0;
    if (currentStep !== "packages" && !bootstrapReviewPackages) return;
    let cancelled = false;
    (async () => {
      setPackagesLoading(true);
      setPackagesError(null);
      try {
        const response = await apiGetWhitelabelPackages(packageQuoteRequest);
        if (cancelled) return;
        if (response?.data?.status !== "RS_OK") {
          setAvailablePackages([]);
          setPackagesError(response?.data?.errorDescription || "Could not load packages.");
          setSelectedPackageId(null);
          return;
        }
        const raw = response?.data?.packages;
        const list: WhitelabelPackage[] = Array.isArray(raw)
          ? raw
              .map((p: any) => mapRawToWhitelabelPackage(p))
              .filter((p: WhitelabelPackage | null): p is WhitelabelPackage => Boolean(p))
          : [];
        setAvailablePackages(list);
        setSelectedPackageId((prev) => {
          const prevStr = String(prev ?? "").trim();
          if (prevStr && list.some((x) => String(x.packageId) === prevStr)) return prevStr;
          return list[0]?.packageId ?? null;
        });
      } catch {
        if (!cancelled) {
          setAvailablePackages([]);
          setPackagesError("Could not load packages.");
          setSelectedPackageId(null);
        }
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isPaymentPrefillFlow,
    currentStep,
    availablePackages.length,
    packageQuoteRequest,
    packagesRetryNonce,
  ]);

  useEffect(() => {
    if (isPaymentPrefillFlow) return;
    if (postSubmitPaymentStep !== "qr") return;
    const wlId = createdWhitelabelId.trim();
    if (!wlId) return;
    if (availablePackages.length > 0) return;
    let cancelled = false;
    (async () => {
      setPackagesLoading(true);
      setPackagesError(null);
      try {
        const userKey = String(userDetails?.UserKey || "").trim();
        if (!userKey) {
          setAvailablePackages([]);
          setPackagesError("Could not load your selected plan.");
          return;
        }
        const response = await apiGetWhitelabelDraft({ userKey, whitelabelId: wlId });
        if (cancelled) return;
        const data = response?.data;
        if (data?.status !== "RS_OK") {
          if (!cancelled) {
            setAvailablePackages([]);
            setPackagesError(
              data?.errorDescription || "Could not load your selected plan."
            );
          }
          return;
        }
        const draft = data?.draft && typeof data.draft === "object" ? data.draft : data;
        const details = draft;
        if (!details || typeof details !== "object") {
          if (!cancelled) {
            setAvailablePackages([]);
            setPackagesError("Could not load your selected plan.");
          }
          return;
        }
        const rawPkg = details.package ?? details.selectedPackage;
        let pkg = mapRawToWhitelabelPackage(rawPkg);
        const detailPkgId = String(details.packageId ?? "").trim();
        if (!pkg && detailPkgId) {
          pkg = mapRawToWhitelabelPackage({
            packageId: detailPkgId,
            name: details.packageName,
            pointsCredited: details.pointsCredited ?? details.points,
            payAmount: details.payAmount,
            originalPayAmount: details.originalPayAmount,
            currency: details.currency ?? details.currencyCode,
            packageDiscountPercent: details.packageDiscountPercent,
          });
        }
        if (pkg?.packageId && !cancelled) {
          setAvailablePackages([pkg]);
          setSelectedPackageId(pkg.packageId);
          setPackagesError(null);
        } else if (!cancelled) {
          setAvailablePackages([]);
          setPackagesError("Could not load your selected plan.");
        }
      } catch {
        if (!cancelled) {
          setAvailablePackages([]);
          setPackagesError("Could not load your selected plan.");
        }
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isPaymentPrefillFlow,
    postSubmitPaymentStep,
    createdWhitelabelId,
    resumePackageId,
    userDetails?.UserKey,
    availablePackages.length,
  ]);

  const domainPreview = useMemo(() => {
    const domainSuffix = normalizeDomainSuffix(selectedDomainSuffix) || "whitelabel.com";
    const safeSubdomain = sanitizeAlphaNumeric(subdomain || "").trim().toLowerCase();
    if (!safeSubdomain) return `https://<subdomain>.${domainSuffix}`;
    return `https://${safeSubdomain}.${domainSuffix}`;
  }, [subdomain, selectedDomainSuffix]);
  const submittedSiteUrl = useMemo(() => {
    const domainSuffix = normalizeDomainSuffix(selectedDomainSuffix) || "whitelabel.com";
    const safeSubdomain = sanitizeAlphaNumeric(subdomain || "").trim().toLowerCase();
    if (!safeSubdomain) return "";
    return `https://${safeSubdomain}.${domainSuffix}`;
  }, [subdomain, selectedDomainSuffix]);

  const wlSubTrim = useMemo(
    () => sanitizeAlphaNumeric(subdomain || "").trim().toLowerCase(),
    [subdomain],
  );
  const domainStatusPhase = useMemo(() => {
    if (subdomain.trim().length > 0 && subdomainHasInvalidChars) return "invalid";
    if (wlSubTrim && wlSubTrim.length < WL_MIN_BRAND_SUB_CHARS) return "short";
    if (wlSubTrim.length >= WL_MIN_BRAND_SUB_CHARS && (domainCheckQueued || domainChecking))
      return "checking";
    if (
      wlSubTrim.length >= WL_MIN_BRAND_SUB_CHARS &&
      !domainCheckQueued &&
      !domainChecking &&
      domainUrlAvailable === false
    )
      return "taken";
    if (
      wlSubTrim.length >= WL_MIN_BRAND_SUB_CHARS &&
      !domainCheckQueued &&
      !domainChecking &&
      domainUrlAvailable === true
    )
      return "available";
    if (wlSubTrim.length >= WL_MIN_BRAND_SUB_CHARS) return "unknown";
    return "idle";
  }, [
    subdomain,
    subdomainHasInvalidChars,
    wlSubTrim,
    domainCheckQueued,
    domainChecking,
    domainUrlAvailable,
  ]);
  useEffect(() => {
    if (!logo) {
      setLogoPreviewUrl("");
      return;
    }
    const next = URL.createObjectURL(logo);
    setLogoPreviewUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [logo]);

  useEffect(() => {
    if (!gif) {
      setGifPreviewUrl("");
      return;
    }
    const next = URL.createObjectURL(gif);
    setGifPreviewUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [gif]);

  /** iOS / Telegram WebView: permission denial often closes the picker with no file; `cancel` + empty `change` may both fire. */
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const gifFileInputRef = useRef<HTMLInputElement>(null);
  const filePickerDismissToastAtRef = useRef(0);
  const notifyPickerDismissedWithoutFile = useCallback(() => {
    const now = Date.now();
    if (now - filePickerDismissToastAtRef.current < 1200) return;
    filePickerDismissToastAtRef.current = now;
    toast.info(
      "Upload did not finish. If the file window closed immediately, allow Photos or Files access for this app in iOS Settings, then tap Upload again.",
      { autoClose: 7000 },
    );
  }, []);

  useEffect(() => {
    if (stepperVisualIdx !== 1) return;
    const logoEl = logoFileInputRef.current;
    const gifEl = gifFileInputRef.current;
    const onCancel = () => notifyPickerDismissedWithoutFile();
    logoEl?.addEventListener("cancel", onCancel);
    gifEl?.addEventListener("cancel", onCancel);
    return () => {
      logoEl?.removeEventListener("cancel", onCancel);
      gifEl?.removeEventListener("cancel", onCancel);
    };
  }, [stepperVisualIdx, notifyPickerDismissedWithoutFile]);

  const handleGenerateLogo = async () => {
    const normalizedWhitelabelName = whitelabelName.trim();
    if (!normalizedWhitelabelName) {
      toast.error("Enter brand name before generating logo.");
      return;
    }
    setGenerateLogoAttemptCount((prev) => prev + 1);
    setIsGeneratingLogo(true);
    try {
      const response = await apiGenerateWhitelabelLogo({
        whitelabelName: normalizedWhitelabelName,
      });
      if (response?.data?.status !== "RS_OK") {
        toast.error(response?.data?.errorDescription || "Failed to generate logo.");
        return;
      }
      const rawData = String(
        response?.data?.logoBase64 ??
          response?.data?.logo ??
          response?.data?.imageBase64 ??
          ""
      ).trim();
      const logoUrl = String(
        response?.data?.logoUrl ?? response?.data?.imageUrl ?? response?.data?.url ?? ""
      ).trim();
      const mime = String(response?.data?.logoType ?? response?.data?.mimeType ?? "image/png");

      if (!rawData && !logoUrl) {
        toast.error("Logo generation returned no image.");
        return;
      }

      const normalizedBase64 = rawData ? stripDataUrlPrefix(rawData) : "";
      setGeneratedLogoBase64(normalizedBase64);
      setGeneratedLogoMime(mime);
      setGeneratedLogoPreviewUrl(
        logoUrl || (normalizedBase64 ? `data:${mime};base64,${normalizedBase64}` : "")
      );
      setLogo(null);
      toast.success("Logo generated.");
    } catch {
      toast.error("Failed to generate logo.");
    } finally {
      setIsGeneratingLogo(false);
    }
  };

  const formatPackagePoints = (n: number) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
  const formatPackagePrice = (amount: number, currency = "INR") => {
    try {
      const locale = currency === "INR" ? "en-IN" : undefined;
      // Show whole currency units only (no fractional .00)
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      const roundedAmount = Math.round(safeAmount);
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(roundedAmount);
    } catch {
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      const roundedAmount = Math.round(safeAmount);
      return `${roundedAmount} ${currency}`;
    }
  };

  const selectedWlPackageLabel = useMemo(() => {
    if (!selectedPackageId) return "—";
    const p = availablePackages.find((x) => x.packageId === selectedPackageId);
    if (!p) return "—";
    const name = p.name?.trim() || "Plan";
    const points = formatPackagePoints(p.pointsCredited ?? 0);
    const price = formatPackagePrice(p.payAmount ?? 0, p.currency || "INR");
    return `${name} - ${points} points - ${price}`;
  }, [selectedPackageId, availablePackages]);

  const packageLoadErrorState = useMemo(() => {
    if (!packagesError) return null;
    const raw = packagesError.trim();
    const lower = raw.toLowerCase();
    if (
      lower.includes("transaction type") ||
      lower.includes("at least one") ||
      lower.includes("fiat") ||
      lower.includes("crypto")
    ) {
      return {
        title: "Could not load plans for this setup.",
        message: "Select at least one payment method (Fiat or Crypto) in Basics, then retry.",
        detail: raw,
      };
    }
    return {
      title: "Could not load plans right now.",
      detail: raw,
    };
  }, [packagesError]);

  const reviewSkinSummary = useMemo(() => {
    if (!selectedSkinId) return "—";
    const sid = String(selectedSkinId).trim();
    const s = skins.find((x) => String(x.skinId ?? "").trim() === sid);
    if (s?.name) return s.name;
    return "—";
  }, [skins, selectedSkinId]);
  const reviewThemeSummary = useMemo(() => {
    if (!themeSelectionRequired) return "";
    return selectedThemeName.trim() || "—";
  }, [themeSelectionRequired, selectedThemeName]);

  const reviewCategoriesSummary = useMemo(() => {
    if (selectedCategories.length === 0) return "—";
    return selectedCategories
      .map((key) => categories.find((c) => c.key === key)?.label || key)
      .join(", ");
  }, [selectedCategories, categories]);

  const reviewTransactionTypesSummary = useMemo(() => {
    const parts: string[] = [];
    if (enableCryptoTransactions) parts.push("Crypto");
    if (enableFiatTransactions) parts.push("Fiat");
    return parts.length > 0 ? parts.join(", ") : "—";
  }, [enableCryptoTransactions, enableFiatTransactions]);
  const reviewWebAddressSummary = useMemo(() => {
    const safeSub = sanitizeAlphaNumeric(subdomain || "").trim().toLowerCase();
    const invalid =
      !subdomain.trim() ||
      subdomainHasInvalidChars ||
      safeSub.length < WL_MIN_BRAND_SUB_CHARS ||
      domainUrlAvailable !== true;
    return invalid ? "—" : domainPreview;
  }, [subdomain, subdomainHasInvalidChars, domainUrlAvailable, domainPreview]);

  const renderWlPackagesPicker = () => (
    <div className="mt-2 border-t border-border-color/40 pt-2">
      <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-violet-600/90">
          <VerifiedRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
        </span>
        <span>
          Your plan <span className="text-red-500">*</span>
        </span>
      </p>
      <p className="mt-0.5 text-xs text-zinc-400">
        Prices follow what you chose above. Tap a card to select.
      </p>
      {packagesLoading ? (
        <p className="mt-1.5 text-xs text-zinc-400">Loading plans…</p>
      ) : null}
      {packagesError && !packagesLoading ? (
        <div className="mt-2 rounded-lg border border-amber-500/35 bg-amber-950/20 p-2.5">
          <p className="text-xs font-semibold text-amber-200">
            {packageLoadErrorState?.title || "Could not load plans."}
          </p>
          <p className="mt-1 text-[11px] text-amber-300/80">
            Details: {packageLoadErrorState?.detail || packagesError}
          </p>
          <button
            type="button"
            className="mt-2 shrink-0 rounded-md border border-amber-400/40 bg-amber-900/20 px-2.5 py-1 text-xs font-medium text-amber-100 hover:bg-amber-800/35"
            onClick={() => setPackagesRetryNonce((n) => n + 1)}
          >
            Retry loading plans
          </button>
        </div>
      ) : null}
      {!packagesLoading ? (
        <WhitelabelPackagePicker
          packages={availablePackages}
          selectedPackageId={selectedPackageId}
          onSelectPackage={setSelectedPackageId}
          formatPrice={formatPackagePrice}
          formatPoints={formatPackagePoints}
        />
      ) : null}
    </div>
  );

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = (reader.result as string) || "";
        resolve(result.includes(",") ? result.split(",")[1] : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const extractSubdomainValue = (raw: unknown) => {
    const input = String(raw ?? "").trim();
    if (!input) return "";
    const withoutProtocol = input.replace(/^https?:\/\//i, "");
    const hostWithPath = withoutProtocol.split("/")[0] || "";
    const host = hostWithPath.split(":")[0] || "";
    const cleanHost = host.replace(/^\.+|\.+$/g, "");
    if (!cleanHost) return "";
    const firstPart = cleanHost.includes(".") ? cleanHost.split(".")[0] || "" : cleanHost;
    return sanitizeAlphaNumeric(firstPart).toLowerCase();
  };
  const extractDomainSuffixValue = (raw: unknown) => {
    const input = String(raw ?? "").trim();
    if (!input) return "";
    const withoutProtocol = input.replace(/^https?:\/\//i, "");
    const hostWithPath = withoutProtocol.split("/")[0] || "";
    const host = hostWithPath.split(":")[0] || "";
    const cleanHost = host.replace(/^\.+|\.+$/g, "");
    if (!cleanHost || !cleanHost.includes(".")) return "";
    const parts = cleanHost.split(".");
    if (parts.length < 2) return "";
    return normalizeDomainSuffix(parts.slice(1).join("."));
  };

  const applySubmittedData = useCallback((submittedData: any) => {
    const draftSkinId = String(submittedData?.skinId ?? "").trim();
    const skinHint =
      draftSkinId ||
      String(submittedData?.skinName ?? "").trim();
    const skinListNow = skinsRef.current;
    setSelectedSkinId(
      skinListNow.length ? resolveSelectedSkinIdForList(skinListNow, skinHint) : skinHint
    );
    const categories = Array.isArray(submittedData?.categories) ? submittedData.categories : [];
    const wlName = String(submittedData?.brandName ?? submittedData?.whitelabelName ?? "").trim();
    const domain = String(submittedData?.brandDomain ?? submittedData?.subdomain ?? "").trim();
    const normalizedSubdomain = extractSubdomainValue(domain);
    const normalizedDomainSuffix = extractDomainSuffixValue(domain);

    setSelectedCategories(categories);

    const submittedThemeId = readSubmittedThemeId(submittedData);
    const submittedThemeConfigs = normalizeThemeConfigs(submittedData?.themeConfigs);
    const shouldApplySubmittedThemeId = submittedThemeId.length > 0;
    if (shouldApplySubmittedThemeId) {
      pendingThemeIdRef.current = submittedThemeId;
    }
    const selectionFromSubmittedThemeId = shouldApplySubmittedThemeId
      ? findThemeSelectionById(themeConfigsRef.current, submittedThemeId) ||
        findThemeSelectionById(submittedThemeConfigs, submittedThemeId)
      : null;
    if (selectionFromSubmittedThemeId) {
      setSelectedThemeCategory(selectionFromSubmittedThemeId.category);
      setActiveThemeBrowseCategory(selectionFromSubmittedThemeId.category);
      setSelectedThemeName(selectionFromSubmittedThemeId.theme.name);
      pendingThemeIdRef.current = "";
    } else if (shouldApplySubmittedThemeId && submittedThemeConfigs.length > 0) {
      const firstCategory = submittedThemeConfigs[0];
      const firstTheme = firstCategory?.themes?.[0];
      setSelectedThemeCategory(firstCategory?.category ?? "");
      setActiveThemeBrowseCategory(firstCategory?.category ?? "");
      setSelectedThemeName(firstTheme?.name ?? "");
    }
    setWhitelabelName(wlName);
    setNameHasInvalidChars(wlName.length > 0 && !/^[a-zA-Z0-9]+$/.test(wlName));
    setSubdomain(normalizedSubdomain);
    if (normalizedDomainSuffix) {
      setSelectedDomainSuffix(normalizedDomainSuffix);
    }
    setSubdomainHasInvalidChars(false);
    setIsSubdomainManuallyEdited(Boolean(normalizedSubdomain));
    setNameAvailable(null);
    setDomainUrlAvailable(null);

    const managePaymentsFlag = submittedData?.managePayments;
    let fiatSource: "own" | "provided" | null =
      managePaymentsFlag !== undefined && managePaymentsFlag !== null
        ? Boolean(managePaymentsFlag)
          ? "own"
          : "provided"
        : null;

    let crypto = Boolean(submittedData?.enableCryptoTransactions);
    const fiatOn = fiatSource === "own" || fiatSource === "provided";
    if (!fiatOn && !crypto) {
      crypto = true;
      fiatSource = "own";
    }
    setEnableFiatTransactions(fiatOn);
    setEnableCryptoTransactions(crypto);
    setFiatBankSource(fiatOn && fiatSource ? fiatSource : null);

    const pkgId = String(submittedData?.packageId ?? "").trim();
    setSelectedPackageId(pkgId.length > 0 ? pkgId : null);

    void hydrateSubmittedBrandAssets(submittedData, {
      setLogo,
      setGeneratedLogoBase64,
      setGeneratedLogoMime,
      setGeneratedLogoPreviewUrl,
      setGif,
      setDraftLoaderBase64,
      setDraftLoaderMime,
      setDraftLoaderPreviewUrl,
    });
  }, []);

  const handleSelectSkin = useCallback((skinId: string) => {
    setSelectedSkinId(skinId);
  }, []);

  const activeThemeCategory = useMemo(
    () => themeConfigs.find((cfg) => cfg.category === activeThemeBrowseCategory) ?? null,
    [themeConfigs, activeThemeBrowseCategory],
  );
  const currentThemePreviewImage = useMemo(() => {
    if (!themePreviewModal.open || themePreviewModal.images.length === 0) return "";
    const safeIndex = Math.max(0, Math.min(themePreviewModal.index, themePreviewModal.images.length - 1));
    return themePreviewModal.images[safeIndex] ?? "";
  }, [themePreviewModal]);

  const selectThemeCard = useCallback((category: string, theme: WlThemeCard) => {
    setSelectedThemeCategory(category);
    setActiveThemeBrowseCategory(category);
    setSelectedThemeName(theme.name);
  }, []);
  const openThemePreviewModal = useCallback((themeName: string, imageUrls: string[]) => {
    const validImages = imageUrls.map((url) => String(url ?? "").trim()).filter((url) => url.length > 0);
    setThemePreviewModal({ open: true, themeName, images: validImages, index: 0 });
  }, []);
  const closeThemePreviewModal = useCallback(() => {
    setThemePreviewModal({ open: false, themeName: "", images: [], index: 0 });
  }, []);
  const navigateThemePreview = useCallback((dir: -1 | 1) => {
    setThemePreviewModal((prev) => {
      if (!prev.open || prev.images.length <= 1) return prev;
      const next = prev.index + dir;
      const wrapped = next < 0 ? prev.images.length - 1 : next >= prev.images.length ? 0 : next;
      return { ...prev, index: wrapped };
    });
  }, []);

  const loadSnapshot = useCallback(async () => {
    const userKey = String(userDetails?.UserKey || "").trim();
    const whitelabelId = String(
      createdWhitelabelId || resumeWhitelabelId || editWhitelabelId || ""
    ).trim();
    if (!whitelabelId) {
      setSnapshotStatus("");
      setSnapshotBalance(null);
      setSupportTicketId("");
      return;
    }
    if (!userKey) {
      return;
    }
    const response = await apiGetWhitelabelDraft({
      userKey,
      whitelabelId,
    });
    const data = response?.data;
    if (data?.status !== "RS_OK") return;

    const payload =
      data?.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data;
    const draft =
      payload?.draft && typeof payload.draft === "object" && !Array.isArray(payload.draft)
        ? payload.draft
        : payload;
    const status: WlStatus = normalizeWlStatus(
      getWhitelabelItemStatusRaw(draft as Record<string, unknown>),
    ) as WlStatus;
    const draftSupportTicketId = String(
      draft?.onboardingSupportTicketId ??
        ""
    ).trim();
    setSnapshotStatus(status);
    setSnapshotBalance(typeof draft?.balance === "number" ? draft.balance : null);
    setSupportTicketId(draftSupportTicketId);

    const mergedForm = buildMergedWhitelabelDraftFormPayload(draft);
    if (mergedForm && Object.keys(mergedForm).length > 0) {
      applySubmittedData(mergedForm);
      if (status === "FAILED") {
        goToStep(steps.indexOf("review"));
      }
    }
  }, [
    userDetails?.UserKey,
    createdWhitelabelId,
    resumeWhitelabelId,
    editWhitelabelId,
    applySubmittedData,
    goToStep,
  ]);
  loadSnapshotForInitRef.current = loadSnapshot;

  const loadSetupData = useCallback(async () => {
    if (isPaymentPrefillFlow) return;
    setSetupError(null);
    try {
      const response = await apiGetSkinsAndCategories();
      const data = response?.data;
      const payload = data?.data && typeof data.data === "object" ? data.data : data;
      const rawSkins = Array.isArray(payload?.skins) ? payload.skins : [];
      const skinList: SkinItem[] = rawSkins.map((s: any): SkinItem | null => {
        const o = s && typeof s === "object" ? { ...s } : {};
        const legacyImages = Array.isArray((o as any).previewImages)
          ? (o as any).previewImages
          : Array.isArray((o as any).previews)
            ? (o as any).previews
            : [];
        const apiPreviews = Array.isArray((o as any).previews)
          ? (o as any).previews
          : [];
        const previewImagesAsObjects = legacyImages.some(
          (p: unknown) =>
            p &&
            typeof p === "object" &&
            ("url" in (p as Record<string, unknown>) || "Url" in (p as Record<string, unknown>))
        );
        const normalizedPreviews =
          apiPreviews.length > 0
            ? apiPreviews
                .map((p: any) => ({
                  url: String(p?.url ?? "").trim(),
                  name: String(p?.name ?? "").trim(),
                }))
                .filter((p: { url: string; name: string }) => p.url.length > 0)
            : previewImagesAsObjects
              ? legacyImages
                  .map((p: any) => ({
                    url: String(p?.url ?? "").trim(),
                    name: String(p?.name ?? "").trim(),
                  }))
                  .filter((p: { url: string; name: string }) => p.url.length > 0)
              : legacyImages
                  .filter((url: unknown) => String(url || "").trim().length > 0)
                  .map((url: unknown) => ({
                    url: String(url),
                    name: "",
                  }));
        const effectivePreviews = normalizedPreviews;
        const skinIdNorm = String((o as any).skinId ?? "").trim();
        const nameNorm = String((o as any).name ?? "").trim();
        const resolvedSkinId = skinIdNorm || nameNorm;
        const categoriesNorm = normalizeCategoryList((o as any).categories);
        if (!resolvedSkinId || !nameNorm || categoriesNorm.length === 0) {
          return null;
        }
        return {
          ...(o as SkinItem),
          skinId: resolvedSkinId,
          name: nameNorm,
          categories: categoriesNorm,
          siteUrl: pickSkinSiteUrl(o),
          tglink: String((o as any)?.tglink ?? "").trim(),
          weblink: String((o as any)?.weblink ?? "").trim(),
          previewImages: previewImagesAsObjects
            ? legacyImages
                .map((p: any) => ({
                  url: String(p?.url ?? "").trim(),
                  name: String(p?.name ?? "").trim(),
                }))
                .filter((p: { url: string; name: string }) => p.url.length > 0)
            : effectivePreviews.map((p: { url: string; name: string }) => p.url),
          previews: effectivePreviews,
          themeSeed: null,
        };
      })
      .filter((row: SkinItem | null): row is SkinItem => Boolean(row));
      const rawAvailableDomains = Array.isArray(data?.availableDomains)
        ? data.availableDomains
        : Array.isArray(payload?.availableDomains)
          ? payload.availableDomains
          : [];
      const domainList = Array.isArray(rawAvailableDomains)
        ? rawAvailableDomains
            .map((d: unknown) => normalizeDomainSuffix(d))
            .filter((d: string) => d.length > 0)
        : [];
      const uniqueDomainList = Array.from(new Set(domainList));
      const parsedThemeConfigs = normalizeThemeConfigs(payload?.themeConfigs);
      setSkins(skinList);
      setThemeConfigs(parsedThemeConfigs);
      setCategories([]);
      setAvailableDomainSuffixes(uniqueDomainList);
      setSelectedDomainSuffix((prev) => {
        const normalizedPrev = normalizeDomainSuffix(prev);
        if (normalizedPrev && uniqueDomainList.includes(normalizedPrev)) return normalizedPrev;
        if (uniqueDomainList.length > 0) return uniqueDomainList[0];
        return "whitelabel.com";
      });
      const apiBad = data?.status !== "RS_OK";

      if (skinList.length === 0 || uniqueDomainList.length === 0 || apiBad) {
        setSetupError("Something went wrong. Please try again later.");
      }
    } catch {
      setSkins([]);
      setThemeConfigs([]);
      setCategories([]);
      setAvailableDomainSuffixes([]);
      setSelectedDomainSuffix("whitelabel.com");
      setSetupError("Something went wrong. Please try again later.");
    }
  }, [isPaymentPrefillFlow]);

  const retrySetupLoad = useCallback(async () => {
    invalidateWlSkinsCategoriesRequestCache();
    wlInitSkinsLoadedRef.current = false;
    wlSetupDataInFlightRef.current = null;
    wlInitSnapshotSigRef.current = null;
    setLoading(true);
    setSetupError(null);
    try {
      await Promise.all([shouldFetchDraftOnInit ? loadSnapshot() : Promise.resolve(), loadSetupData()]);
      wlInitSkinsLoadedRef.current = true;
      const uk = String(userDetails?.UserKey || "").trim();
      const wid = String(createdWhitelabelId || resumeWhitelabelId || editWhitelabelId || "").trim();
      if (uk && wid) wlInitSnapshotSigRef.current = `${uk}::${wid}`;
    } catch {
      toast.error("Unable to load whitelabel data.");
    } finally {
      setLoading(false);
    }
  }, [
    shouldFetchDraftOnInit,
    loadSnapshot,
    loadSetupData,
    userDetails?.UserKey,
    createdWhitelabelId,
    resumeWhitelabelId,
    editWhitelabelId,
  ]);

  useEffect(() => {
    if (isPaymentPrefillFlow) return;
    if (!forceEditRequested || !editPrefillData) return;
    applySubmittedData(editPrefillData);
    const prefillStatus = String(editPrefillData?.whitelabelStatus ?? "IN_PROGRESS")
      .trim()
      .toUpperCase() as WlStatus;
    setSnapshotStatus(prefillStatus);
  }, [isPaymentPrefillFlow, forceEditRequested, editPrefillData, applySubmittedData]);

  useEffect(() => {
    if (!isPaymentPrefillFlow || !paymentPrefillData) return;
    const prefillPkgId = String(paymentPrefillData?.packageId ?? "").trim();
    const prefillPkg: WhitelabelPackage | null = prefillPkgId
      ? {
          packageId: prefillPkgId,
          name: String(paymentPrefillData?.packageName ?? "Plan"),
          pointsCredited: Number(
            paymentPrefillData?.pointsCredited ?? paymentPrefillData?.points ?? 0
          ),
          payAmount: Number(paymentPrefillData?.payAmount ?? 0),
          discountAmount: (() => {
            const v = paymentPrefillData?.discountAmount;
            if (v == null || v === "") return undefined;
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
          })(),
          originalPayAmount: (() => {
            const v = paymentPrefillData?.originalPayAmount;
            if (v == null || v === "") return undefined;
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
          })(),
          currency: String(paymentPrefillData?.currency ?? "INR"),
          packageDiscountPercent: (() => {
            const v = paymentPrefillData?.packageDiscountPercent;
            if (v == null || v === "") return undefined;
            const n = Number(v);
            return Number.isFinite(n) ? n : undefined;
          })(),
          paymentMethods: parseWhitelabelPackagePaymentMethods(paymentPrefillData),
        }
      : null;
    setLoading(false);
    setSetupError(null);
    setCreatedWhitelabelId(String(paymentPrefillData?.whitelabelId ?? resumeWhitelabelId ?? "").trim());
    if (prefillPkg) {
      setAvailablePackages([prefillPkg]);
      setSelectedPackageId(prefillPkg.packageId);
      setPackagesError(null);
    }
    setPostSubmitPaymentStep("qr");
  }, [isPaymentPrefillFlow, paymentPrefillData, resumeWhitelabelId]);

  useEffect(() => {
    if (isPaymentPrefillFlow) {
      invalidateWlSkinsCategoriesRequestCache();
      wlInitSkinsLoadedRef.current = false;
      wlSetupDataInFlightRef.current = null;
      wlInitSnapshotSigRef.current = null;
      setLoading(false);
      setSetupError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSetupError(null);

    (async () => {
      try {
        if (!wlInitSkinsLoadedRef.current) {
          if (!wlSetupDataInFlightRef.current) {
            wlSetupDataInFlightRef.current = loadSetupData().finally(() => {
              wlSetupDataInFlightRef.current = null;
            });
          }
          await wlSetupDataInFlightRef.current;
          /** Must run even when `cancelled` (Strict Mode / effect re-run): otherwise a completed fetch leaves the ref false and the next run hits the API again. */
          wlInitSkinsLoadedRef.current = true;
        }
        if (cancelled) return;

        if (shouldFetchDraftOnInit) {
          const userKey = String(userDetails?.UserKey || "").trim();
          const wlId = String(createdWhitelabelId || resumeWhitelabelId || editWhitelabelId || "").trim();
          const sig = `${userKey}::${wlId}`;
          if (userKey && wlId && wlInitSnapshotSigRef.current !== sig) {
            await loadSnapshotForInitRef.current();
            wlInitSnapshotSigRef.current = sig;
          }
        }
      } catch {
        if (!cancelled) toast.error("Unable to load whitelabel data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isPaymentPrefillFlow,
    shouldFetchDraftOnInit,
    loadSetupData,
    userDetails?.UserKey,
    createdWhitelabelId,
    resumeWhitelabelId,
    editWhitelabelId,
  ]);

  useEffect(() => {
    if (isPaymentPrefillFlow) return;
    if (loading) return;
    if (!resumePaymentRequested) return;
    if (!resumeWhitelabelId) return;
    if (paymentResumeInitializedRef.current) return;

    paymentResumeInitializedRef.current = true;
    setCreatedWhitelabelId(resumeWhitelabelId);
    if (resumePackageId) {
      setSelectedPackageId((prev) => prev || resumePackageId);
    }
    setPostSubmitPaymentStep("qr");
  }, [isPaymentPrefillFlow, loading, resumePaymentRequested, resumeWhitelabelId, resumePackageId]);

  useEffect(() => {
    if (!forceEditRequested) return;
    if (!editWhitelabelId) return;
    setCreatedWhitelabelId(editWhitelabelId);
    setPostSubmitPaymentStep(null);
  }, [forceEditRequested, editWhitelabelId]);

  /** After skins load: match saved id or skin name to a list entry, else first skin. */
  useEffect(() => {
    if (readOnlySnapshot) return;
    if (!skins.length) return;
    setSelectedSkinId((prev) => resolveSelectedSkinIdForList(skins, prev));
  }, [skins, readOnlySnapshot]);

  /** Categories come from selected skin; keep only keys still valid for that skin. */
  useEffect(() => {
    if (!skins.length || !selectedSkinId) {
      setCategories([]);
      return;
    }
    const selectedSkin = skins.find(
      (s) => String(s.skinId ?? "").trim() === String(selectedSkinId ?? "").trim()
    );
    const nextCategories =
      selectedSkin && Array.isArray(selectedSkin.categories) ? selectedSkin.categories : [];
    setCategories(nextCategories);
    const allowed = new Set(nextCategories.map((cat) => cat.key));
    setSelectedCategories((prev) => prev.filter((key) => allowed.has(key)));
  }, [skins, selectedSkinId]);

  useEffect(() => {
    if (!themeConfigs.length) {
      setSelectedThemeCategory("");
      setActiveThemeBrowseCategory("");
      setSelectedThemeName("");
      return;
    }
    const selectionFromPendingId = findThemeSelectionById(themeConfigs, pendingThemeIdRef.current);
    if (selectionFromPendingId) {
      setSelectedThemeCategory(selectionFromPendingId.category);
      setActiveThemeBrowseCategory(selectionFromPendingId.category);
      setSelectedThemeName(selectionFromPendingId.theme.name);
      pendingThemeIdRef.current = "";
      return;
    }
    const selectedCategory = themeConfigs.find((cfg) => cfg.category === selectedThemeCategory);
    const selectedTheme = selectedCategory?.themes.find((theme) => theme.name === selectedThemeName);
    if (selectedCategory && selectedTheme) {
      setActiveThemeBrowseCategory(selectedCategory.category);
      return;
    }
    setSelectedThemeCategory(themeConfigs[0].category);
    setActiveThemeBrowseCategory(themeConfigs[0].category);
    setSelectedThemeName(themeConfigs[0].themes[0]?.name ?? "");
  }, [themeConfigs, selectedThemeCategory, selectedThemeName]);

  useEffect(() => {
    if (!selectedThemeCategory || !selectedThemeName) return;
    const selectedCategory = themeConfigs.find((cfg) => cfg.category === selectedThemeCategory);
    const selectedTheme = selectedCategory?.themes.find((theme) => theme.name === selectedThemeName);
    if (!selectedTheme) return;
    selectThemeCard(selectedThemeCategory, selectedTheme);
  }, [themeConfigs, selectedThemeCategory, selectedThemeName, selectThemeCard]);

  /** When plans load or change, keep selection if it exists in the list; else first plan. */
  useEffect(() => {
    if (readOnlySnapshot) return;
    if (!availablePackages.length) return;
    setSelectedPackageId((prev) => {
      const prevStr = String(prev ?? "").trim();
      if (prevStr && availablePackages.some((x) => String(x.packageId) === prevStr)) {
        return prevStr;
      }
      return availablePackages[0]?.packageId ?? null;
    });
  }, [availablePackages, readOnlySnapshot]);

  /** Default all categories selected when none chosen (snapshot/submitted data keeps its value). */
  useEffect(() => {
    if (readOnlySnapshot) return;
    if (!categories.length) return;
    const allKeys = categories.map((c) => c.key);
    setSelectedCategories((prev) => (prev.length > 0 ? prev : allKeys));
  }, [categories, readOnlySnapshot]);

  availabilityWlIdRef.current = String(
    createdWhitelabelId || editWhitelabelId || resumeWhitelabelId || ""
  ).trim();

  const triggerNameAvailabilityCheck = useCallback((rawName: string, hasInvalidChars: boolean) => {
    if (nameCheckTimerRef.current != null) {
      clearTimeout(nameCheckTimerRef.current);
      nameCheckTimerRef.current = null;
    }
    if (isPaymentPrefillFlow || isSubmitting || postSubmitPaymentStep || readOnlySnapshot) return;
    const nameTrim = String(rawName || "").trim();
    const canCheck = nameTrim.length >= WL_MIN_BRAND_SUB_CHARS && !hasInvalidChars;
    if (!canCheck) {
      setNameCheckQueued(false);
      setNameChecking(false);
      setNameAvailable(null);
      setNameAvailabilityError("");
      lastCheckedNameRef.current = null;
      return;
    }
    if (nameTrim === lastCheckedNameRef.current) {
      setNameCheckQueued(false);
      return;
    }
    nameCheckTimerRef.current = window.setTimeout(async () => {
      setNameAvailabilityError("");
      setNameCheckQueued(true);
      setNameChecking(true);
      nameAvailabilityAbortRef.current?.abort();
      const controller = new AbortController();
      nameAvailabilityAbortRef.current = controller;
      const reqId = ++nameAvailabilityReqIdRef.current;
      try {
        const response = await apiCheckWhitelabelName(
          {
            whitelabelName: nameTrim,
            whitelabelId: availabilityWlIdRef.current,
          },
          { signal: controller.signal }
        );
        if (nameAvailabilityReqIdRef.current !== reqId || controller.signal.aborted) return;
        if (response?.data?.status !== "RS_OK") {
          setNameAvailable(null);
          setNameAvailabilityError(String(response?.data?.errorDescription || "Could not verify this name."));
          return;
        }
        setNameAvailable(Boolean(response?.data?.isAvailable));
        setNameAvailabilityError("");
        lastCheckedNameRef.current = nameTrim;
      } catch {
        if (controller.signal.aborted) return;
        if (nameAvailabilityReqIdRef.current === reqId) {
          setNameAvailable(null);
          setNameAvailabilityError("Could not verify this name.");
        }
      } finally {
        if (controller.signal.aborted) {
          setNameCheckQueued(false);
          setNameChecking(false);
        } else if (nameAvailabilityReqIdRef.current === reqId) {
          setNameCheckQueued(false);
          setNameChecking(false);
        }
        if (nameAvailabilityAbortRef.current === controller) {
          nameAvailabilityAbortRef.current = null;
        }
      }
    }, WL_NAME_DOMAIN_CHECK_DEBOUNCE_MS);
  }, [isPaymentPrefillFlow, isSubmitting, postSubmitPaymentStep, readOnlySnapshot]);

  const triggerSubdomainAvailabilityCheck = useCallback((rawSubdomain: string, hasInvalidChars: boolean) => {
    if (subdomainCheckTimerRef.current != null) {
      clearTimeout(subdomainCheckTimerRef.current);
      subdomainCheckTimerRef.current = null;
    }
    if (isPaymentPrefillFlow || isSubmitting || postSubmitPaymentStep || readOnlySnapshot) return;
    const subSan = sanitizeAlphaNumeric(rawSubdomain).trim().toLowerCase();
    const canCheck = subSan.length >= WL_MIN_BRAND_SUB_CHARS && !hasInvalidChars;
    if (!canCheck) {
      setDomainCheckQueued(false);
      setDomainChecking(false);
      setDomainUrlAvailable(null);
      setDomainAvailabilityError("");
      lastCheckedSubRef.current = null;
      return;
    }
    if (subSan === lastCheckedSubRef.current) {
      setDomainCheckQueued(false);
      return;
    }
    subdomainCheckTimerRef.current = window.setTimeout(async () => {
      setDomainAvailabilityError("");
      setDomainCheckQueued(true);
      setDomainChecking(true);
      subdomainAvailabilityAbortRef.current?.abort();
      const controller = new AbortController();
      subdomainAvailabilityAbortRef.current = controller;
      const reqId = ++subdomainAvailabilityReqIdRef.current;
      try {
        const response = await apiCheckWhitelabelSubdomain(
          {
            subdomain: subSan,
            whitelabelId: availabilityWlIdRef.current,
          },
          { signal: controller.signal }
        );
        if (subdomainAvailabilityReqIdRef.current !== reqId || controller.signal.aborted) return;
        if (response?.data?.status !== "RS_OK") {
          setDomainUrlAvailable(null);
          setDomainAvailabilityError(
            String(response?.data?.errorDescription || "Could not verify this address.")
          );
          return;
        }
        setDomainUrlAvailable(Boolean(response?.data?.isAvailable));
        setDomainAvailabilityError("");
        lastCheckedSubRef.current = subSan;
      } catch {
        if (controller.signal.aborted) return;
        if (subdomainAvailabilityReqIdRef.current === reqId) {
          setDomainUrlAvailable(null);
          setDomainAvailabilityError("Could not verify this address.");
        }
      } finally {
        if (controller.signal.aborted) {
          setDomainCheckQueued(false);
          setDomainChecking(false);
        } else if (subdomainAvailabilityReqIdRef.current === reqId) {
          setDomainCheckQueued(false);
          setDomainChecking(false);
        }
        if (subdomainAvailabilityAbortRef.current === controller) {
          subdomainAvailabilityAbortRef.current = null;
        }
      }
    }, WL_NAME_DOMAIN_CHECK_DEBOUNCE_MS);
  }, [isPaymentPrefillFlow, isSubmitting, postSubmitPaymentStep, readOnlySnapshot]);

  useEffect(() => {
    if (!forceEditRequested) return;
    if (isPaymentPrefillFlow || isSubmitting || postSubmitPaymentStep || readOnlySnapshot) return;
    const wlId = String(createdWhitelabelId || editWhitelabelId || "").trim();
    if (!wlId) return;
    const nameTrim = whitelabelName.trim();
    const subTrim = sanitizeAlphaNumeric(subdomain).trim().toLowerCase();
    const seed = `${wlId}::${nameTrim}::${subTrim}`;
    if (editInitialAvailabilitySeedRef.current === seed) return;
    editInitialAvailabilitySeedRef.current = seed;
    if (nameTrim.length >= WL_MIN_BRAND_SUB_CHARS && !nameHasInvalidChars) {
      triggerNameAvailabilityCheck(nameTrim, false);
    }
    if (subTrim.length >= WL_MIN_BRAND_SUB_CHARS && !subdomainHasInvalidChars) {
      triggerSubdomainAvailabilityCheck(subTrim, false);
    }
  }, [
    forceEditRequested,
    isPaymentPrefillFlow,
    isSubmitting,
    postSubmitPaymentStep,
    readOnlySnapshot,
    createdWhitelabelId,
    editWhitelabelId,
    whitelabelName,
    nameHasInvalidChars,
    subdomain,
    subdomainHasInvalidChars,
    triggerNameAvailabilityCheck,
    triggerSubdomainAvailabilityCheck,
  ]);

  useEffect(() => {
    if (!isSubmitting && !postSubmitPaymentStep) return;
    nameAvailabilityReqIdRef.current += 1;
    subdomainAvailabilityReqIdRef.current += 1;
    nameAvailabilityAbortRef.current?.abort();
    nameAvailabilityAbortRef.current = null;
    subdomainAvailabilityAbortRef.current?.abort();
    subdomainAvailabilityAbortRef.current = null;
    if (nameCheckTimerRef.current != null) {
      clearTimeout(nameCheckTimerRef.current);
      nameCheckTimerRef.current = null;
    }
    if (subdomainCheckTimerRef.current != null) {
      clearTimeout(subdomainCheckTimerRef.current);
      subdomainCheckTimerRef.current = null;
    }
    setNameCheckQueued(false);
    setNameChecking(false);
    setNameAvailabilityError("");
    setDomainCheckQueued(false);
    setDomainChecking(false);
    setDomainAvailabilityError("");
  }, [isSubmitting, postSubmitPaymentStep]);

  useEffect(() => {
    return () => {
      nameAvailabilityAbortRef.current?.abort();
      subdomainAvailabilityAbortRef.current?.abort();
      if (nameCheckTimerRef.current != null) clearTimeout(nameCheckTimerRef.current);
      if (subdomainCheckTimerRef.current != null) clearTimeout(subdomainCheckTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!postSubmitPaymentStep) return;
    setWhitelabelName("");
    setSubdomain("");
    setNameHasInvalidChars(false);
    setSubdomainHasInvalidChars(false);
    setIsSubdomainManuallyEdited(false);
    setNameAvailable(null);
    setNameAvailabilityError("");
    setDomainUrlAvailable(null);
    setDomainAvailabilityError("");
    setLogo(null);
    setGif(null);
    setGeneratedLogoBase64("");
    setGeneratedLogoMime("");
    setGeneratedLogoPreviewUrl("");
    setDraftLoaderBase64("");
    setDraftLoaderMime("");
    setDraftLoaderPreviewUrl("");
  }, [postSubmitPaymentStep]);

  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIdx]);

  const handleShareContactTelegram = () => {
    try {
      (window as any)?.Telegram?.WebApp?.ready();
      (window as any)?.Telegram?.WebApp?.requestContact((contact: any) => {
        if (contact) {
          toast.success("Contact shared successfully.");
          setTimeout(() => {
            dispatch(getRefId() as any);
          }, 600);
        }
      });
    } catch {
      toast.error("Unable to share contact.");
    }
  };

  const handleSendOtp = async () => {
    if (!/^\d{10}$/.test(webMobile)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    try {
      const response = await apiPost("/auth/send-otp", {
        userId: "",
        mobileNumber: webMobile,
        countryCode: "91",
        groupBotId: "1844",
      });
      if (response?.data?.status !== "RS_OK") {
        toast.error(response?.data?.errorDescription || "Failed to send OTP.");
        return;
      }
      setOtpToken(response?.data?.token || "");
      toast.success("OTP sent successfully.");
    } catch {
      toast.error("Failed to send OTP.");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpToken || otp.length < 4) {
      toast.error("Please enter OTP.");
      return;
    }
    try {
      const response = await apiPost("/auth/verify-otp", {
        otp,
        token: otpToken,
      });
      if (response?.data?.status !== "RS_OK") {
        toast.error(response?.data?.errorDescription || "Invalid OTP.");
        return;
      }
      setContactVerifiedByWebOtp(true);
      toast.success("Contact verified.");
    } catch {
      toast.error("OTP verification failed.");
    }
  };

  const handleNext = () => {
    setStepIdx((prev) => {
      const next = Math.min(prev + 1, steps.length - 1);
      if (next !== prev) stepTransitionDirRef.current = 1;
      return next;
    });
  };

  const handlePrev = () => {
    setStepIdx((prev) => {
      const next = Math.max(prev - 1, 0);
      if (next !== prev) stepTransitionDirRef.current = -1;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!enableFiatTransactions && !enableCryptoTransactions) {
      toast.error("Select at least one payment method: Fiat or Crypto.");
      return;
    }
    const logoPv = generatedLogoPreviewUrl.trim();
    const hasLogoForSubmit =
      Boolean(logo) ||
      generatedLogoBase64.trim().length > 0 ||
      /^https?:\/\//i.test(logoPv) ||
      /^data:/i.test(logoPv) ||
      (logoPv.startsWith("/") && logoPv.length > 1);
    if (!hasLogoForSubmit) {
      toast.error("Logo is mandatory. Upload or generate one before submitting.");
      return;
    }
    if (!allMandatoryFilled) {
      return;
    }
    const subSanitized = sanitizeAlphaNumeric(subdomain).trim().toLowerCase();
    if (
      whitelabelName.trim().length < WL_MIN_BRAND_SUB_CHARS ||
      subSanitized.length < WL_MIN_BRAND_SUB_CHARS ||
      subdomainHasInvalidChars
    ) {
      toast.error(
        `Brand name and web address need at least ${WL_MIN_BRAND_SUB_CHARS} valid characters each.`
      );
      return;
    }
    if (!selectedPackageId || availablePackages.length === 0) {
      toast.error("Choose a plan before submitting.");
      return;
    }
    const selectedPackageSnapshot =
      availablePackages.find((pkg) => pkg.packageId === selectedPackageId) ?? null;
    setIsSubmitting(true);
    nameAvailabilityReqIdRef.current += 1;
    subdomainAvailabilityReqIdRef.current += 1;
    nameAvailabilityAbortRef.current?.abort();
    nameAvailabilityAbortRef.current = null;
    subdomainAvailabilityAbortRef.current?.abort();
    subdomainAvailabilityAbortRef.current = null;
    try {
      const managePaymentsOwn = enableFiatTransactions && fiatBankSource === "own";
      let logoBase64 = logo ? await fileToBase64(logo) : generatedLogoBase64.trim();
      let logoTypeOut = logo?.type || generatedLogoMime || "";
      if (!logoBase64) {
        const preview = generatedLogoPreviewUrl.trim();
        if (preview.startsWith("data:")) {
          const parsed = parseDraftLogoOrLoaderInput(
            preview,
            logoTypeOut || generatedLogoMime || "image/png"
          );
          if (parsed.kind === "inline") {
            logoBase64 = parsed.base64;
            logoTypeOut = parsed.mime;
            setGeneratedLogoBase64(parsed.base64);
            setGeneratedLogoMime(parsed.mime);
          }
        } else if (/^https?:\/\//i.test(preview) || preview.startsWith("/")) {
          try {
            const fetched = await fetchRemoteImageAsBase64(preview);
            logoBase64 = fetched.base64;
            logoTypeOut = fetched.mime || logoTypeOut || "image/png";
            setGeneratedLogoBase64(fetched.base64);
            setGeneratedLogoMime(logoTypeOut);
          } catch {
            toast.error(
              "Logo could not be loaded from the saved link. Upload or generate a logo."
            );
            return;
          }
        }
      }
      if (!logoBase64.trim()) {
        toast.error("Logo is mandatory. Upload or generate one before submitting.");
        return;
      }

      let loaderBase64 = gif ? await fileToBase64(gif) : draftLoaderBase64.trim();
      let loaderTypeOut = gif?.type || draftLoaderMime || "";
      if (!loaderBase64) {
        const lp = draftLoaderPreviewUrl.trim();
        if (lp.startsWith("data:")) {
          const parsed = parseDraftLogoOrLoaderInput(
            lp,
            loaderTypeOut || draftLoaderMime || "image/gif"
          );
          if (parsed.kind === "inline") {
            loaderBase64 = parsed.base64;
            loaderTypeOut = parsed.mime;
            setDraftLoaderBase64(parsed.base64);
            setDraftLoaderMime(parsed.mime);
          }
        } else if (/^https?:\/\//i.test(lp) || lp.startsWith("/")) {
          try {
            const fetched = await fetchRemoteImageAsBase64(lp);
            loaderBase64 = fetched.base64;
            loaderTypeOut = fetched.mime || loaderTypeOut || "image/gif";
            setDraftLoaderBase64(fetched.base64);
            setDraftLoaderMime(loaderTypeOut);
          } catch {
            /*
             * Keep original loader input when URL fetch fails (e.g. CORS).
             * This prevents update requests from dropping previously saved GIF info.
             */
            loaderBase64 = lp;
            loaderTypeOut = loaderTypeOut || draftLoaderMime || "image/gif";
          }
        }
      }
      let faviconBase64 = "";
      let faviconMime = "";
      try {
        const fav = await fetchPublicImageAsBase64(DEFAULT_WL_FAVICON_PATH);
        faviconBase64 = fav.base64;
        faviconMime = fav.mime;
      } catch {
        /* keep empty if the default asset cannot be loaded */
      }
      const payload: WhitelabelCreateUpdatePayload = {
        whitelabelName: whitelabelName.trim(),
        subdomain: submittedSiteUrl,
        skinId: selectedSkinId,
        categories: selectedCategories,
        enableCryptoTransactions,
        enableFiatTransactions,
        logo: logoBase64,
        logoType: logoTypeOut,
        loader: loaderBase64,
        loaderType: loaderTypeOut,
        favicon: faviconBase64,
        faviconType: faviconMime,
        packageId: selectedPackageId,
        managePayments: managePaymentsOwn,
      };
      if (selectedThemeCategory && selectedThemeName) {
        const selectedCategory = themeConfigs.find((cfg) => cfg.category === selectedThemeCategory);
        const selectedTheme = selectedCategory?.themes.find((theme) => theme.name === selectedThemeName);
        if (selectedCategory && selectedTheme) {
          if (selectedTheme.themeId) {
            payload.themeId = selectedTheme.themeId;
          }
        }
      }

      const isUpdate = Boolean(createdWhitelabelId.trim());
      if (isUpdate) {
        const response = await apiUpdateWhitelabel({
          ...payload,
          whitelabelId: createdWhitelabelId.trim(),
        });
        if (response?.data?.status !== "RS_OK") {
          toast.error(
            response?.data?.errorDescription || "Failed to update whitelabel."
          );
          return;
        }
        toast.success("Whitelabel updated.");
        navigate("/whitelabel");
        return;
      }

      const response = await apiCreateWhitelabel(payload);
      if (response?.data?.status !== "RS_OK") {
        toast.error(
          response?.data?.errorDescription ||
            "Failed to create whitelabel."
        );
        return;
      }
      const wlId = String(
        response?.data?.whitelabelId ?? ""
      ).trim();
      if (!wlId) {
        toast.error("Create succeeded but no whitelabel id was returned.");
        return;
      }
      setPostSubmitPaymentStep("qr");
      setCreatedWhitelabelId(wlId);
      setPaymentPackageSnapshot(selectedPackageSnapshot);
    } catch {
      toast.error("Failed to create whitelabel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyPaymentText = useCallback((value: string) => {
    const text = String(value || "").trim();
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied."),
      () => toast.error("Could not copy.")
    );
  }, []);

  const removePaymentScreenshot = useCallback(() => {
    if (paymentScreenshotPreviewUrl) {
      URL.revokeObjectURL(paymentScreenshotPreviewUrl);
    }
    setPaymentScreenshotPreviewUrl("");
    setPaymentScreenshotFile(null);
    setPaymentUtr("");
    setPaymentImageUrl("");
    setPaymentUtrFieldVisible(false);
    const fileInput = document.getElementById("wl-payment-screenshot") as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  }, [paymentScreenshotPreviewUrl]);

  const loadWlCryptoWalletAddress = useCallback((_curr: WlCryptoCurrencyItem) => {
    setWlCryptoWalletLoading(true);
    setWlCryptoWalletAddress("");
    const whitelabelId = String(createdWhitelabelId || "").trim();
    if (!whitelabelId) {
      toast.error("Whitelabel ID missing. Please create whitelabel first.");
      setWlCryptoWalletLoading(false);
      return;
    }
    const packageId = String(
      selectedPackageId || resumePackageId || availablePackages[0]?.packageId || ""
    ).trim();
    if (!packageId) {
      toast.error("A plan must be selected and loaded to continue to payment.");
      setWlCryptoWalletLoading(false);
      return;
    }
    (async () => {
      try {
        const response = await apiGetWlWalletAddress({
          currency: _curr?.cryptoCurrency,
          networkId: String(_curr?.networkId ?? ""),
          packageId,
          whitelabelId,
        });
        const data = response?.data;
        if (data?.status && data?.status !== "RS_OK") {
          toast.error(data?.errorDescription || "Could not get deposit address.");
          return;
        }
        const addr = String(data?.walletAddress ?? "").trim();
        if (!addr) {
          toast.error("Deposit address was empty.");
          return;
        }
        setWlCryptoWalletAddress(addr);
      } catch {
        toast.error("Could not get deposit address.");
      } finally {
        setWlCryptoWalletLoading(false);
      }
    })();
  }, [availablePackages, createdWhitelabelId, resumePackageId, selectedPackageId]);

  useEffect(() => {
    if (!isPaymentOptionLocked || paymentFlowKind !== "crypto") return;
    let cancelled = false;
    (async () => {
      setWlCryptoCurrLoading(true);
      setWlCryptoCurrList([]);
      setSelectedWlCryptoCurrency(null);
      setWlCryptoWalletAddress("");
      try {
        const response = await apiGetWlCryptoCurrencies();
        if (cancelled) return;
        const list = response?.data?.currenciesList;
        setWlCryptoCurrList(Array.isArray(list) ? [...list] : []);
      } catch {
        if (!cancelled) toast.error("Could not load crypto currencies.");
      } finally {
        if (!cancelled) setWlCryptoCurrLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPaymentOptionLocked, paymentFlowKind]);

  useEffect(() => {
    if (isPaymentOptionLocked) return;
    setPaymentFlowKind("fiat");
    setWlCryptoCurrList([]);
    setSelectedWlCryptoCurrency(null);
    setWlCryptoWalletAddress("");
  }, [isPaymentOptionLocked]);

  const extractWhitelabelUtrFromOcr = useCallback(
    async (file: File, depositType: string) => {
      setPaymentOcrLoading(true);
      try {
        const fileToBase64 = (nextFile: File): Promise<string> =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = String(reader.result || "");
              resolve(result.includes(",") ? result.split(",")[1] : result);
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(nextFile);
          });

        const formData = new FormData();
        formData.append("image", await fileToBase64(file));
        formData.append("image_names", file.name);
        formData.append("image_types", file.type);
        formData.append("request", JSON.stringify({ payment_method: depositType }));

        const response = await OCR_API.post("/ocr/get-utr", formData);
        const data = response?.data;
        const rawUtr =
          typeof data === "string"
            ? data
            : data && typeof data === "object"
              ? (data as any).utr
              : "";
        const extractedImageUrl =
          data && typeof data === "object"
            ? String((data as any).imageUrl ?? "").trim()
            : "";

        if (extractedImageUrl) {
          setPaymentImageUrl(extractedImageUrl);
        }

        const normalizedUtr = String(rawUtr ?? "")
          .replace(/\D/g, "")
          .slice(0, 12);

        if (normalizedUtr) {
          setPaymentUtr(normalizedUtr);
          toast.success("UTR extracted.");
        } else {
          toast.warning("Could not extract UTR. Please enter it manually.");
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Could not extract UTR. Please enter it manually.");
      } finally {
        setPaymentOcrLoading(false);
        setPaymentUtrFieldVisible(true);
      }
    },
    []
  );

  const handlePaymentScreenshotChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file.");
        return;
      }

      if (paymentScreenshotPreviewUrl) {
        URL.revokeObjectURL(paymentScreenshotPreviewUrl);
      }

      const previewUrl = URL.createObjectURL(file);
      setPaymentScreenshotPreviewUrl(previewUrl);
      setPaymentScreenshotFile(file);
      setPaymentUtr("");
      setPaymentImageUrl("");
      setPaymentUtrFieldVisible(false);

      const depositType = paymentTransferTab === "bankTransfer" ? "BANK_TRANSFER" : "UPI";
      await extractWhitelabelUtrFromOcr(file, depositType);
    },
    [extractWhitelabelUtrFromOcr, paymentScreenshotPreviewUrl, paymentTransferTab]
  );

  const handlePaymentFlowContinueToQr = useCallback(async () => {
    const wlId = String(createdWhitelabelId || "").trim();
    if (!wlId) return;
    const packageIdForDeposit = String(
      selectedPackageId || resumePackageId || availablePackages[0]?.packageId || ""
    ).trim();
    if (!packageIdForDeposit) {
      toast.error("A plan must be selected and loaded to continue to payment.");
      return;
    }
    setPaymentQrLoading(true);
    setPaymentInitError(null);
    try {
      const response = await apiInitiateDeposit({
        whitelabelId: wlId,
        packageId: packageIdForDeposit,
      });
      if (response?.data?.status !== "RS_OK") {
        const msg = response?.data?.errorDescription || "Could not load payment QR.";
        setPaymentInitError(msg);
        toast.error(msg);
        return;
      }
      const paymentPayload =
        response?.data?.data && typeof response.data.data === "object"
          ? response.data.data
          : {};
      const paymentOptions = Array.isArray(paymentPayload?.payment_options)
        ? paymentPayload.payment_options
        : [];
      const firstString = (value: unknown): string => {
        if (Array.isArray(value)) return String(value[0] ?? "").trim();
        return String(value ?? "").trim();
      };
      const normalizeQrList = (value: unknown): string[] => {
        const values = Array.isArray(value) ? value : value ? [value] : [];
        return values
          .map((item) => String(item ?? "").trim())
          .filter(Boolean)
          .map((item) =>
            item.startsWith("http") || item.startsWith("data:") ? item : `data:image/png;base64,${item}`
          );
      };
      const upiOption = paymentOptions.find(
        (x: any) =>
          String(x?.payment_method ?? "")
            .trim()
            .toUpperCase() === "UPI"
      );
      const bankOption = paymentOptions.find((x: any) => {
        const method = String(x?.payment_method ?? "")
          .trim()
          .toUpperCase();
        return method === "BANK_TRANSFER" || method === "BANK" || method === "NEFT" || method === "IMPS";
      });
      const upiDetails =
        (upiOption?.payment_method_details && typeof upiOption.payment_method_details === "object"
          ? upiOption.payment_method_details
            : {}) || {};
      const bankDetails =
        (bankOption?.payment_method_details && typeof bankOption.payment_method_details === "object"
          ? bankOption.payment_method_details
            : {}) || {};
      const upiId = firstString(upiDetails?.upi_id);
      const upiIntent = String(upiDetails?.upi_intent ?? "").trim();
      const resolvedQrImages = normalizeQrList(
        upiDetails?.upi_qr ??
          paymentPayload?.qr_image_url ??
          paymentPayload?.qr_image_data
      );
      const nextBankDetails: PaymentBankDetails = {
        accountNumber: firstString(
          bankDetails?.bank_account_number
        ),
        accountName: firstString(
          bankDetails?.bank_account_holder
        ),
        ifsc: firstString(
          bankDetails?.ifsc_code
        ),
        bankId: firstString(
          bankDetails?.bank_id ??
            paymentPayload?.bank_id
        ),
        bankName: firstString(bankDetails?.bank_name),
      };
      const hasBankDetails = Object.values(nextBankDetails).some(Boolean);
      const hasUpiDetails = Boolean(upiId || resolvedQrImages.length > 0 || upiIntent);

      setPaymentRefId(
        String(
            paymentPayload?.reference_id ??
            paymentPayload?.ref_id ??
            ""
        ).trim()
      );
      setPaymentBankId(
        nextBankDetails.bankId || String(paymentPayload?.bank_id ?? "").trim()
      );
      setPaymentDepositType(
        String(paymentPayload?.deposit_type ?? "UPI").trim() ||
          "UPI"
      );
      setPaymentTgProviderId(
        String(paymentPayload?.tg_provider_id ?? "").trim()
      );
      setPaymentUpiId(upiId);
      setPaymentUpiIntent(upiIntent);
      setPaymentQrImages(resolvedQrImages);
      setPaymentBankDetails(hasBankDetails ? nextBankDetails : null);
      setSelectedPaymentOption("upiBank");
      setPaymentFlowKind("fiat");
      setIsPaymentOptionLocked(true);
      setPaymentTransferTab(hasUpiDetails ? "upi" : "bankTransfer");
      setPaymentImageUrl("");
      setPaymentUtr("");
      removePaymentScreenshot();
      if (!hasUpiDetails && !hasBankDetails) {
        const msg = "Payment details were empty.";
        setPaymentInitError(msg);
        toast.error(msg);
        return;
      }
      setPaymentInitError(null);
      setPostSubmitPaymentStep("qr");
    } catch {
      const msg = "Could not load payment QR.";
      setPaymentInitError(msg);
      toast.error(msg);
    } finally {
      setPaymentQrLoading(false);
    }
  }, [
    availablePackages,
    createdWhitelabelId,
    removePaymentScreenshot,
    resumePackageId,
    selectedPackageId,
  ]);

  const handleConfirmPaymentMethodAndInitiate = useCallback(async () => {
    const pkgForPayment =
      paymentPackageSnapshot ??
      availablePackages.find((p) => String(p.packageId) === String(selectedPackageId)) ??
      null;
    if (
      selectedPaymentOption === "crypto" &&
      !packagePaymentMethodCodesInclude(pkgForPayment?.paymentMethods, "CRYPTO")
    ) {
      toast.error("Crypto is not available for this plan.");
      return;
    }
    if (
      selectedPaymentOption === "upiBank" &&
      !packagePaymentMethodCodesInclude(pkgForPayment?.paymentMethods, "FIAT")
    ) {
      toast.error("UPI / bank transfer is not available for this plan.");
      return;
    }
    if (selectedPaymentOption === "offline") {
      if (isActiveWhitelabelPaymentFlow) {
        toast.error("Offline payment is not available from this flow.");
        return;
      }
      setPaymentInitError(null);
      setPaymentFlowKind("fiat");
      setIsPaymentOptionLocked(true);
      return;
    }
    if (selectedPaymentOption === "crypto") {
      setPaymentInitError(null);
      setPaymentFlowKind("crypto");
      setIsPaymentOptionLocked(true);
      return;
    }
    await handlePaymentFlowContinueToQr();
  }, [
    availablePackages,
    handlePaymentFlowContinueToQr,
    paymentPackageSnapshot,
    selectedPackageId,
    selectedPaymentOption,
    isActiveWhitelabelPaymentFlow,
  ]);

  const handleOfflinePaymentRequestSubmit = useCallback(async () => {
    if (paymentOfflineSubmitting) return;
    const wlId = createdWhitelabelId.trim();
    if (!wlId) {
      toast.error("Something went wrong. Please go back and try again.");
      return;
    }
    setPaymentOfflineSubmitting(true);
    try {
      await apiUpdateWhitelabelPaymentMethod({
        whitelabelId: wlId,
        paymentMethod: "OFFLINE",
      });
      toast.success("Request submitted.");
      navigate(`/whitelabel/details/${encodeURIComponent(wlId)}?mode=pending`, {
        state: { paymentPending: true },
      });
    } catch {
      toast.error("Something went wrong. Please go back and try again.");
    } finally {
      setPaymentOfflineSubmitting(false);
    }
  }, [createdWhitelabelId, navigate, paymentOfflineSubmitting]);

  useEffect(() => {
    return () => {
      if (paymentScreenshotPreviewUrl) {
        URL.revokeObjectURL(paymentScreenshotPreviewUrl);
      }
    };
  }, [paymentScreenshotPreviewUrl]);

  const handlePaymentUtrSubmit = async () => {
    const utr = paymentUtr.trim();
    const uKey = String(userDetails?.UserKey || "").trim();
    if (!createdWhitelabelId) return;
    if (!selectedPackageId) {
      toast.error("Plan is missing. Use Edit to return to the form and select a plan.");
      return;
    }
    if (!uKey) {
      toast.error("User key is missing.");
      return;
    }
    if (!paymentRefId) {
      toast.error("Payment reference is missing. Please regenerate QR and try again.");
      return;
    }
    if (!utr) {
      toast.error("Enter the UTR (Unique Transaction Reference) from your payment.");
      return;
    }
    if (!/^\d{12}$/.test(utr)) {
      toast.error("UTR must be exactly 12 digits.");
      return;
    }
    if (!paymentScreenshotFile) {
      toast.error("Upload the payment screenshot first.");
      return;
    }
    setPaymentUtrSubmitting(true);
    try {
      const resolvedDepositType = paymentTransferTab === "bankTransfer" ? "BANK_TRANSFER" : "UPI";
      const resolvedBankId =
        paymentTransferTab === "bankTransfer"
          ? paymentBankDetails?.bankId || paymentBankId
          : paymentBankId;
      const response = await apiProcessDeposit({
        bankId: resolvedBankId,
        depositType: resolvedDepositType || paymentDepositType || "UPI",
        imageUrl: paymentImageUrl,
        refId: paymentRefId,
        tgProviderId: paymentTgProviderId,
        uKey,
        utr,
      });
      if (response?.data?.status !== "RS_OK") {
        toast.error(response?.data?.errorDescription || "Could not save UTR.");
        return;
      }
      toast.success("UTR submitted.");
      setPostSubmitPaymentStep(null);
      setPaymentRefId("");
      setPaymentBankId("");
      setPaymentDepositType("");
      setPaymentTgProviderId("");
      setPaymentImageUrl("");
      setPaymentUpiId("");
      setPaymentUpiIntent("");
      setPaymentQrImages([]);
      setPaymentBankDetails(null);
      setPaymentPackageSnapshot(null);
      setSelectedPaymentOption("upiBank");
      setIsPaymentOptionLocked(false);
      setPaymentTransferTab("upi");
      removePaymentScreenshot();
      setPaymentUtr("");
      navigate("/whitelabel");
    } catch {
      toast.error("Could not save UTR.");
    } finally {
      setPaymentUtrSubmitting(false);
    }
  };

  const selectedPaymentPackageResolved = useMemo(
    () =>
      paymentPackageSnapshot ??
      availablePackages.find((pkg) => String(pkg.packageId) === String(selectedPackageId)) ??
      null,
    [paymentPackageSnapshot, availablePackages, selectedPackageId]
  );

  const packageAllowsCryptoForPayment = useMemo(
    () => packagePaymentMethodCodesInclude(selectedPaymentPackageResolved?.paymentMethods, "CRYPTO"),
    [selectedPaymentPackageResolved]
  );

  const packageAllowsFiatForPayment = useMemo(
    () => packagePaymentMethodCodesInclude(selectedPaymentPackageResolved?.paymentMethods, "FIAT"),
    [selectedPaymentPackageResolved]
  );

  useLayoutEffect(() => {
    if (!postSubmitPaymentStep) return;
    if (isPaymentOptionLocked) return;

    const showOffline = !isActiveWhitelabelPaymentFlow;
    const selectionIsValid =
      (selectedPaymentOption === "crypto" && packageAllowsCryptoForPayment) ||
      (selectedPaymentOption === "upiBank" && packageAllowsFiatForPayment) ||
      (selectedPaymentOption === "offline" && showOffline);

    if (selectionIsValid) return;

    const next: PaymentOptionKey = packageAllowsCryptoForPayment
      ? "crypto"
      : packageAllowsFiatForPayment
        ? "upiBank"
        : showOffline
          ? "offline"
          : "upiBank";

    if (next !== selectedPaymentOption) setSelectedPaymentOption(next);
  }, [
    postSubmitPaymentStep,
    isPaymentOptionLocked,
    isActiveWhitelabelPaymentFlow,
    packageAllowsCryptoForPayment,
    packageAllowsFiatForPayment,
    selectedPaymentOption,
  ]);

  const toggleCategory = (key: string) => {
    setCategoryTouched(true);
    setSelectedCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleTransactionType = (key: TransactionTypeKey) => {
    const { fiat, crypto } = txTypesRef.current;
    if (key === "FIAT") {
      if (fiat) {
        setFiatBankSource(null);
        setEnableFiatTransactions(false);
      } else {
        setEnableFiatTransactions(true);
        setFiatBankSource("own");
      }
      return;
    }
    if (crypto) {
      setEnableCryptoTransactions(false);
    } else {
      setEnableCryptoTransactions(true);
    }
  };

  if (loading) {
    return (
      <div className="text-white px-3 sm:px-4 py-[5rem]">
        <div className="mx-auto max-w-md text-center">
          <p className="text-lg font-medium text-zinc-100">Loading whitelabel setup…</p>
          <p className="mt-2 text-sm text-zinc-400">Fetching skins, categories, and your saved progress.</p>
        </div>
      </div>
    );
  }

  if (setupError) {
    return (
      <div className="text-white px-3 sm:px-4 py-[5rem] pb-[8rem]">
        <div className="mx-auto max-w-md rounded-xl border border-amber-500/40 bg-amber-950/20 p-5 text-center">
          <p className="text-base font-semibold text-amber-100">Setup couldn't load</p>
          <p className="mt-2 text-sm leading-relaxed text-amber-100/85">{setupError}</p>
          <button
            type="button"
            className={`mt-5 w-full ${BTN_PRIMARY_CLASS}`}
            onClick={() => void retrySetupLoad()}
          >
            Retry
          </button>
          <button
            type="button"
            className={`mt-2 w-full ${BTN_SECONDARY_CLASS}`}
            onClick={() => navigate("/whitelabel")}
          >
            Back to whitelabels
          </button>
        </div>
      </div>
    );
  }

  const goBackFromPayment = () => {
    // If we're on the payment screen and a method is already chosen, first unlock
    // back to the payment options instead of leaving the flow.
    if (postSubmitPaymentStep && isPaymentOptionLocked) {
      setIsPaymentOptionLocked(false);
      setPaymentTransferTab("upi");
      setPaymentFlowKind("fiat");
      removePaymentScreenshot();
      setPaymentUtr("");
      setPaymentUtrFieldVisible(false);
      return;
    }

    const id = createdWhitelabelId.trim();
    if (paymentReturnTo) {
      navigate(paymentReturnTo);
      return;
    }
    if (id) navigate(`/whitelabel/details/${encodeURIComponent(id)}?mode=pending`);
    else navigate("/whitelabel");
  };

  if (postSubmitPaymentStep) {
    const selectedPaymentPackage = selectedPaymentPackageResolved;
    const paymentOptionCards: Array<{
      key: PaymentOptionKey;
      title: string;
      subtitle: string;
      badge?: string;
      enabled: boolean;
    }> = [
      {
        key: "crypto",
        title: "Crypto",
        subtitle: "Instant, fast, and safe transactions.",
        badge: "+5% off",
        enabled: packageAllowsCryptoForPayment,
      },
      {
        key: "upiBank",
        title: "Fiat",
        subtitle: "UPI / bank transfer",
        enabled: packageAllowsFiatForPayment,
      },
      ...(isActiveWhitelabelPaymentFlow
        ? []
        : [
            {
              key: "offline" as const,
              title: "Offline Payment",
              subtitle: "",
              enabled: true,
            },
          ]),
    ];
    const hasPaymentQr = paymentQrImages.length > 0;
    const hasPaymentUpi = Boolean(paymentUpiId || paymentUpiIntent);
    const hasPaymentBank = Boolean(
      paymentBankDetails &&
        Object.values(paymentBankDetails).some((value) => String(value || "").trim() !== "")
    );
    const isValidPaymentUtr = /^\d{12}$/.test(paymentUtr.trim());
    const selectedOptionMeta = paymentOptionCards.find((x) => x.key === selectedPaymentOption);
    const payAmount = selectedPaymentPackage?.payAmount ?? 0;
    const discountAmountForPayment = selectedPaymentPackage?.discountAmount ?? 0;
    const originalPayAmountForPayment = selectedPaymentPackage?.originalPayAmount ?? 0;
    const cryptoChosen = isPaymentOptionLocked
      ? paymentFlowKind === "crypto"
      : selectedPaymentOption === "crypto";
    const totalAmountForPayment =
      originalPayAmountForPayment > 0 ? originalPayAmountForPayment : payAmount;
    const discountedPayableAmount =
      discountAmountForPayment > 0 ? discountAmountForPayment : payAmount;
    const hasCryptoDiscount =
      cryptoChosen &&
      totalAmountForPayment > 0 &&
      discountedPayableAmount > 0 &&
      discountedPayableAmount < totalAmountForPayment;
    const displayAmountForPayment = hasCryptoDiscount ? discountedPayableAmount : payAmount;
    const payCurrency = selectedPaymentPackage?.currency ?? "INR";
    const cryptoRate =
      paymentFlowKind === "crypto"
        ? selectedWlCryptoCurrency?.conversions?.deposit_exchange_rate ?? null
        : null;
    const cryptoAmount =
      cryptoRate && displayAmountForPayment
        ? displayAmountForPayment / cryptoRate
        : null;

    const paymentFlowTitle =
      postSubmitPaymentStep === "qr" && isPaymentOptionLocked
        ? paymentFlowKind === "crypto"
          ? "Crypto"
          : selectedOptionMeta?.title || "UPI / Bank Transfer"
        : "Complete payment";

    return (
      <div
        className="text-white mt-[3.75rem] px-3 pb-0 pt-4 sm:px-4 sm:pt-5"
        style={{ paddingBottom: `calc(${footerHeightPx + footerClearancePx}px + 1rem)` }}
      >
        <div className="mx-auto max-w-lg">
          <div className="mb-2 flex items-center gap-3">
            <button
              type="button"
              onClick={goBackFromPayment}
              className="bg-inputbox-color flex h-8 w-8 shrink-0 items-center justify-center rounded"
              aria-label="Go back"
              title="Back"
            >
              <ArrowBackIcon
                className="text-white"
                style={{ width: "20px", height: "20px", color: "#FFFFFF" }}
              />
            </button>
            <h2
              className={`min-w-0 text-lg font-semibold leading-tight text-zinc-100 ${
                isPaymentOptionLocked ? "uppercase tracking-wide" : ""
              }`}
            >
              {paymentFlowTitle}
            </h2>
          </div>
          {selectedPaymentPackage && isPaymentOptionLocked ? (
            <div className="mb-1 flex justify-center">
              <div className="inline-flex max-w-[min(100%,20rem)] min-w-0 flex-col items-center rounded-xl bg-inputbox-color/90 px-4 py-2.5 text-center">
                <p className="max-w-full truncate text-base font-semibold leading-tight text-white sm:text-lg">
                  {selectedPaymentPackage.name?.trim() || "Plan"}
                </p>
                <p className="mt-2 text-3xl font-extrabold leading-tight text-white tabular-nums">
                  {formatPackagePrice(displayAmountForPayment, payCurrency)}
                </p>
                {paymentFlowKind === "crypto" && hasCryptoDiscount ? (
                  <>
                    <p className="mt-0.5 text-base font-medium text-zinc-300">
                      <span className="line-through decoration-[1.5px] decoration-zinc-400/90">
                        {formatPackagePrice(totalAmountForPayment, payCurrency)}
                      </span>
                    </p>
                  </>
                ) : null}
                <p className="mt-1 text-sm tabular-nums text-zinc-400">
                  {formatPackagePoints(selectedPaymentPackage.pointsCredited ?? 0)} points
                </p>
                {paymentFlowKind === "crypto" && cryptoAmount != null && selectedWlCryptoCurrency ? (
                  <p className="mt-2 rounded-full border border-emerald-400/40 bg-emerald-500/12 px-3 py-1 text-xs font-semibold text-emerald-200">
                    ≈ {cryptoAmount.toFixed(6)} {selectedWlCryptoCurrency.cryptoCurrency}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="px-0 pb-6 sm:pb-8">
          {postSubmitPaymentStep === "qr" ? (
            <>
              {!isPaymentOptionLocked && paymentInitError ? (
                <div className="mt-2 rounded-lg border border-amber-500/35 bg-amber-950/20 px-3 py-2 text-sm text-amber-100/90">
                  {paymentInitError}
                </div>
              ) : null}
              {!isPaymentOptionLocked ? (
                <>
                  {selectedPaymentPackage ? (
                    <div className="mt-1 w-full">
                      <div className="mx-auto w-fit max-w-[min(100%,20rem)] min-w-0 rounded-xl border border-white/[0.1] bg-inputbox-color px-4 py-2.5 text-center shadow-[0_8px_28px_rgba(0,0,0,0.35)] sm:px-5">
                        <p className="max-w-full truncate text-base font-semibold leading-tight text-white sm:text-lg">
                          {selectedPaymentPackage.name?.trim() || "Plan"}
                        </p>
                        <p className="mt-2 text-3xl font-extrabold leading-tight text-white tabular-nums">
                          {formatPackagePrice(
                            displayAmountForPayment,
                            selectedPaymentPackage.currency
                          )}
                        </p>
                        {selectedPaymentOption === "crypto" && hasCryptoDiscount ? (
                          <p className="mt-0.5 text-base font-medium text-zinc-300">
                            <span className="line-through decoration-[1.5px] decoration-zinc-400/90">
                              {formatPackagePrice(
                                totalAmountForPayment,
                                selectedPaymentPackage.currency
                              )}
                            </span>
                          </p>
                        ) : null}
                        <p className="mt-1 text-sm tabular-nums text-zinc-400">
                          {formatPackagePoints(selectedPaymentPackage.pointsCredited ?? 0)} points
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-4 grid gap-2.5">
                    {paymentOptionCards.map((option) => {
                      const isActive = selectedPaymentOption === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          aria-disabled={!option.enabled}
                          onKeyDown={(e) => {
                            if (!option.enabled && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              if (option.key === "crypto") {
                                toast.info("Crypto is not available for this plan.");
                              } else if (option.key === "upiBank") {
                                toast.info("UPI / bank transfer is not available for this plan.");
                              } else {
                                toast.info(`${option.title} will be enabled next.`);
                              }
                            }
                          }}
                          onClick={() => {
                            if (!option.enabled) {
                              if (option.key === "crypto") {
                                toast.info("Crypto is not available for this plan.");
                              } else if (option.key === "upiBank") {
                                toast.info("UPI / bank transfer is not available for this plan.");
                              } else {
                                toast.info(`${option.title} will be enabled next.`);
                              }
                              return;
                            }
                            setSelectedPaymentOption(option.key);
                          }}
                          className={`rounded-xl border px-4 py-3 text-left transition ${
                            isActive
                              ? "border-[color-mix(in_srgb,var(--border-color)_75%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_12%,transparent)]"
                              : "border-border-color bg-inputbox-color/70"
                          } ${!option.enabled ? "cursor-not-allowed opacity-60" : ""}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                              <span
                                className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-xs ${
                                  isActive
                                    ? "border-[color-mix(in_srgb,var(--border-color)_80%,transparent)]"
                                    : "border-zinc-500/70"
                                }`}
                                aria-hidden
                              >
                                <span
                                  className={`h-2.5 w-2.5 rounded-full ${
                                    isActive
                                      ? "bg-[color-mix(in_srgb,var(--border-color)_90%,#22c55e)]"
                                      : "bg-transparent"
                                  }`}
                                />
                              </span>
                              <div className="min-w-0 pr-1">
                                <p className="text-sm font-semibold text-zinc-100">{option.title}</p>
                                {option.subtitle ? (
                                  <p
                                    className={`mt-0.5 text-xs leading-snug ${
                                      isActive
                                        ? "text-[color-mix(in_srgb,var(--border-color)_62%,#e4e4e7)]"
                                        : "text-zinc-400/95"
                                    }`}
                                  >
                                    {option.subtitle}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                            {option.badge ? (
                              <span className="shrink-0 self-center whitespace-nowrap rounded-full border border-emerald-400/45 bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold leading-none text-emerald-200 sm:px-3.5 sm:py-2 sm:text-sm">
                                {option.badge}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    disabled={paymentQrLoading}
                    className={`mt-4 w-full ${BTN_PRIMARY_CLASS}`}
                    onClick={() => void handleConfirmPaymentMethodAndInitiate()}
                  >
                    {paymentQrLoading ? "Loading…" : "Continue"}
                  </button>
                </>
              ) : null}

              {isPaymentOptionLocked && paymentFlowKind === "fiat" && (
                <div className="mt-3 text-sm">
                {selectedPaymentOption === "offline" ? (
                  <div className="mx-auto mt-2 w-full max-w-[320px]">
                    <div className="rounded-lg border border-border-color bg-inputbox-color/70 px-4 py-3">
                      <p className="text-sm font-semibold text-zinc-100">Raise offline payment request</p>
                      <p className="mt-2 text-xs leading-relaxed text-zinc-300">
                        Submit this request and our team will reach out to help you complete the
                        payment offline.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={paymentOfflineSubmitting}
                      className={`mt-4 w-full ${BTN_PRIMARY_CLASS}`}
                      onClick={() => void handleOfflinePaymentRequestSubmit()}
                    >
                      {paymentOfflineSubmitting ? "Submitting..." : "Submit request"}
                    </button>
                  </div>
                ) : (
                <>
                <div className="flex justify-center gap-4 text-sm">
                  <button
                    type="button"
                    onClick={() => hasPaymentUpi && setPaymentTransferTab("upi")}
                    disabled={!hasPaymentUpi}
                    className={`flex flex-col items-center ${
                      paymentTransferTab === "upi"
                        ? "text-[color-mix(in_srgb,var(--border-color)_85%,#e5e7eb)]"
                        : "text-zinc-400"
                    } ${!hasPaymentUpi ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <span className="text-center font-bold">UPI</span>
                    <span
                      className={`mt-1 h-[2px] w-36 rounded-full ${
                        paymentTransferTab === "upi"
                          ? "bg-[color-mix(in_srgb,var(--border-color)_85%,#22d3ee)]"
                          : "bg-transparent"
                      }`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => hasPaymentBank && setPaymentTransferTab("bankTransfer")}
                    disabled={!hasPaymentBank}
                    className={`flex flex-col items-center ${
                      paymentTransferTab === "bankTransfer"
                        ? "text-[color-mix(in_srgb,var(--border-color)_85%,#e5e7eb)]"
                        : "text-zinc-400"
                    } ${!hasPaymentBank ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <span className="text-center font-bold">BANK TRANSFER</span>
                    <span
                      className={`mt-1 h-[2px] w-36 rounded-full ${
                        paymentTransferTab === "bankTransfer"
                          ? "bg-[color-mix(in_srgb,var(--border-color)_85%,#22d3ee)]"
                          : "bg-transparent"
                      }`}
                    />
                  </button>
                </div>

                {paymentTransferTab === "upi" && hasPaymentUpi ? (
                  <div className="mt-4 text-sm text-zinc-200">
                    {hasPaymentQr && (
                      <div className="mb-4 flex justify-center">
                        <div className="mx-auto aspect-square w-[min(190px,calc(100vw-2rem))] overflow-hidden rounded-md bg-white">
                          <img
                            src={paymentQrImages[0]}
                            alt="Payment QR"
                            className="h-full w-full origin-center scale-[1.08] object-contain object-center select-none"
                            draggable={false}
                          />
                        </div>
                      </div>
                    )}
                    {paymentUpiId ? (
                      <div className="mt-3 flex items-center justify-center gap-2 text-sm">
                        <span className="font-bold text-zinc-300">UPI ID:</span>
                        <span className="break-all font-bold text-zinc-50">{paymentUpiId}</span>
                        <button
                          type="button"
                          onClick={() => copyPaymentText(paymentUpiId)}
                          className="inline-flex items-center justify-center"
                        >
                          <ContentCopyIcon sx={{ fontSize: 18, color: "#d4d4d8" }} />
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {paymentTransferTab === "bankTransfer" && hasPaymentBank && paymentBankDetails ? (
                  <div className="mx-auto mt-4 flex w-full max-w-[280px] flex-col gap-3 px-0.5 text-left text-sm font-bold text-zinc-100">
                    {(
                      [
                        { label: "Account Number", value: paymentBankDetails.accountNumber, copy: true },
                        { label: "Account Name", value: paymentBankDetails.accountName, copy: true },
                        { label: "IFSC Code", value: paymentBankDetails.ifsc, copy: true },
                        { label: "Bank Name", value: paymentBankDetails.bankName, copy: true },
                      ] as const
                    ).map((row) => {
                      const raw = String(row.value ?? "").trim();
                      const display = raw || "—";
                      const canCopy = row.copy && Boolean(raw);
                      return (
                        <div key={row.label} className="flex flex-wrap items-baseline gap-x-1 gap-y-1 leading-snug">
                          <span className="text-zinc-200">{row.label}: </span>
                          <span
                            className={`min-w-0 break-all ${raw ? "text-white" : "text-zinc-500"}`}
                          >
                            {display}
                          </span>
                          {canCopy ? (
                            <button
                              type="button"
                              onClick={() => copyPaymentText(raw)}
                              className="inline-flex shrink-0 translate-y-0.5 text-zinc-300 hover:text-white"
                              aria-label={`Copy ${row.label}`}
                            >
                              <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <div className="mx-auto mt-5 flex w-full max-w-[280px] flex-col">
                  <p className="text-left text-sm font-bold text-zinc-200">
                    Payment screenshot <span className="text-red-400">*</span>
                  </p>
                  <input
                    id="wl-payment-screenshot"
                    type="file"
                    accept="image/*"
                    onChange={handlePaymentScreenshotChange}
                    className="hidden"
                    aria-label="Payment screenshot upload"
                  />
                  <label
                    htmlFor="wl-payment-screenshot"
                    className={`mt-2 flex w-full cursor-pointer items-center gap-2 rounded-lg border border-border-color bg-border-color px-4 py-2.5 text-sm font-bold text-white shadow-sm ${
                      paymentOcrLoading ? "pointer-events-none opacity-70" : ""
                    } ${
                      paymentOcrLoading || !paymentScreenshotFile ? "justify-center" : "justify-between"
                    }`}
                  >
                    <CloudUploadRoundedIcon sx={{ fontSize: 18, color: "#fff", flexShrink: 0 }} />
                    <span
                      className={`min-w-0 font-bold ${
                        paymentOcrLoading || !paymentScreenshotFile
                          ? "text-center"
                          : "flex-1 truncate text-left"
                      }`}
                    >
                      {paymentOcrLoading
                        ? "Please wait"
                        : paymentScreenshotFile?.name?.trim() || "Upload"}
                    </span>
                    {paymentScreenshotFile && !paymentOcrLoading ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removePaymentScreenshot();
                        }}
                        className="shrink-0"
                        aria-label="Remove screenshot"
                      >
                        <CancelIcon sx={{ fontSize: 18, color: "#e5e7eb" }} />
                      </button>
                    ) : null}
                  </label>
                  {paymentScreenshotPreviewUrl ? (
                    <div className="mt-3 w-full overflow-hidden rounded-lg border border-border-color">
                      <img
                        src={paymentScreenshotPreviewUrl}
                        alt="Payment screenshot preview"
                        className="max-h-[140px] w-full object-contain"
                      />
                    </div>
                  ) : null}

                  {paymentUtrFieldVisible ? (
                    <>
                      <label
                        htmlFor="wl-payment-utr"
                        className="mt-5 text-left text-sm font-bold text-zinc-200"
                      >
                        UTR Number
                      </label>
                      <input
                        id="wl-payment-utr"
                        className="mt-1.5 w-full p-2.5 text-lg text-white bg-inputbox-color rounded-lg border border-border-color"
                        value={paymentUtr}
                        onChange={(e) => setPaymentUtr(e.target.value.replace(/\D/g, "").slice(0, 12))}
                        placeholder="e.g. 123456789012"
                        autoComplete="off"
                        inputMode="numeric"
                      />
                    </>
                  ) : null}

                  <div className="mt-4 flex w-full">
                    <button
                      type="button"
                      disabled={
                        paymentUtrSubmitting || paymentOcrLoading || !paymentScreenshotFile || !isValidPaymentUtr
                      }
                      className={`flex-1 min-w-0 ${BTN_PRIMARY_CLASS}`}
                      onClick={handlePaymentUtrSubmit}
                    >
                      {paymentUtrSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
                </>
                )}
              </div>
              )}

              {isPaymentOptionLocked && paymentFlowKind === "crypto" ? (
                <div className="mx-auto mt-2 w-full max-w-[320px] text-sm">
                  <label htmlFor="wl-crypto-currency" className="text-sm font-semibold text-zinc-200">
                    Select currency
                  </label>
                  {wlCryptoCurrLoading ? (
                    <p className="mt-2 text-zinc-400">Loading currencies…</p>
                  ) : wlCryptoCurrList.length === 0 ? (
                    <p className="mt-2 text-zinc-400">No crypto currency found.</p>
                  ) : (
                    <select
                      id="wl-crypto-currency"
                      className="mt-2 w-full rounded-lg border border-border-color bg-inputbox-color px-3 py-2.5 text-sm font-medium text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[var(--border-color)]"
                      value={
                        selectedWlCryptoCurrency
                          ? `${selectedWlCryptoCurrency.cryptoCurrency}:::${selectedWlCryptoCurrency.networkId}`
                          : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) {
                          setSelectedWlCryptoCurrency(null);
                          setWlCryptoWalletAddress("");
                          return;
                        }
                        const sep = ":::";
                        const i = v.indexOf(sep);
                        if (i < 0) return;
                        const cc = v.slice(0, i);
                        const nid = v.slice(i + sep.length);
                        const curr = wlCryptoCurrList.find(
                          (c) => c.cryptoCurrency === cc && String(c.networkId) === nid
                        );
                        if (curr) {
                          setSelectedWlCryptoCurrency(curr);
                          void loadWlCryptoWalletAddress(curr);
                        }
                      }}
                    >
                      <option value="">Select a currency</option>
                      {wlCryptoCurrList.map((c) => (
                        <option
                          key={`${c.cryptoCurrency}-${c.networkId}`}
                          value={`${c.cryptoCurrency}:::${c.networkId}`}
                        >
                          {`${c.cryptoCurrency} (${c.blockChain})`}
                        </option>
                      ))}
                    </select>
                  )}

                  {selectedWlCryptoCurrency ? (
                    <>
                      <span className="mt-3 block text-sm font-semibold text-zinc-200">
                        Your deposit address ({selectedWlCryptoCurrency.cryptoCurrency})
                      </span>
                      {wlCryptoWalletLoading ? (
                        <p className="mt-2 text-zinc-400">Loading address…</p>
                      ) : wlCryptoWalletAddress ? (
                        <>
                          <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-border-color bg-inputbox-color px-3 py-2.5">
                            <span className="min-w-0 truncate text-xs text-zinc-100">
                              {wlCryptoWalletAddress}
                            </span>
                            <button
                              type="button"
                              onClick={() => copyPaymentText(wlCryptoWalletAddress)}
                              className="shrink-0 text-zinc-300 hover:text-white"
                              aria-label="Copy address"
                            >
                              <ContentCopyIcon sx={{ fontSize: 18 }} />
                            </button>
                          </div>
                          <div className="mt-3 flex justify-center">
                            <div className="rounded-md bg-white p-1">
                              <QRCode value={wlCryptoWalletAddress} size={128} />
                            </div>
                          </div>
                          <p className="mt-2 text-center text-xs text-zinc-400">
                            Scan the QR code or copy the address to complete your transfer.
                          </p>
                        </>
                      ) : (
                        <p className="mt-2 text-xs text-zinc-500">
                          Select a currency to load your deposit address.
                        </p>
                      )}
                    </>
                  ) : null}

                </div>
              ) : null}
            </>
          ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (readOnlySnapshot) {
    return (
      <div className="text-white px-3 sm:px-4 py-[5rem] pb-[8rem]">
        <div className="bg-inputbox-color border border-border-color rounded-xl p-4">
          {supportTicketId ? (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-1.5 text-white transition-colors hover:bg-white/10"
                onClick={() => setSupportChatOpen(true)}
                aria-label="Open customer support chat"
                title="Customer support"
              >
                <img
                  src={SupportIcon}
                  alt=""
                  className="h-6 w-6"
                  style={{
                    filter:
                      "invert(100%) sepia(100%) saturate(0%) hue-rotate(139deg) brightness(110%) contrast(101%)",
                  }}
                />
              </button>
            </div>
          ) : null}
          <h2 className="text-2xl font-semibold">Whitelabel Status: {snapshotStatus}</h2>
          <p className="text-lg text-zinc-400 mt-1">
            You cannot edit this request. Refresh to see latest updates.
          </p>
          <p className="mt-3">Balance: {snapshotBalance ?? "-"}</p>
          <p className="mt-1">Name: {whitelabelName || "-"}</p>
          <p className="mt-1">Domain: {domainPreview}</p>
          <button
            type="button"
            className="mt-4 p-2.5 w-full text-lg text-white bg-border-color rounded-lg border border-border-color"
            onClick={loadSnapshot}
          >
            Refresh Status
          </button>
        </div>
        <WhitelabelSupportChatPopup
          isOpen={supportChatOpen}
          onClose={() => setSupportChatOpen(false)}
          ticketId={supportTicketId}
        />
      </div>
    );
  }

  if (!canUseStepper) {
    return (
      <div className="text-white px-3 sm:px-4 py-[5rem] pb-[8rem]">
        <div className="bg-inputbox-color border border-border-color rounded-xl p-4">
          <h2 className="text-2xl font-semibold">Contact verification required</h2>
          <p className="text-lg text-zinc-400 mt-1">
            You cannot create a whitelabel until contact is verified.
          </p>

          {isTelegram ? (
            <button
              type="button"
              className="mt-4 p-2.5 w-full text-lg text-white bg-border-color rounded-lg border border-border-color"
              onClick={handleShareContactTelegram}
            >
              Share Contact
            </button>
          ) : (
            <>
              <input
                className="mt-4 p-2.5 w-full text-lg text-white bg-inputbox-color rounded-lg border border-border-color"
                value={webMobile}
                onChange={(e) => setWebMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Enter mobile number"
              />
              <button
                type="button"
                className="mt-3 p-2.5 w-full text-lg text-white bg-border-color rounded-lg border border-border-color"
                onClick={handleSendOtp}
              >
                Send OTP
              </button>
              <input
                className="mt-3 p-2.5 w-full text-lg text-white bg-inputbox-color rounded-lg border border-border-color"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter OTP"
              />
              <button
                type="button"
                className="mt-3 p-2.5 w-full text-lg text-white bg-border-color rounded-lg border border-border-color"
                onClick={handleVerifyOtp}
              >
                Verify OTP
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="text-white box-border mt-[3.75rem] flex flex-col overflow-x-visible overflow-y-hidden px-3 pb-0 pt-2 sm:px-4 sm:pt-3"
      style={{
        height: `calc(100dvh - 3.75rem - ${footerHeightPx + footerClearancePx}px)`,
        maxHeight: `calc(100dvh - 3.75rem - ${footerHeightPx + footerClearancePx}px)`,
      }}
    >
      {themePreviewModal.open ? (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 p-4"
          onClick={closeThemePreviewModal}
          role="dialog"
          aria-modal="true"
          aria-label={`Preview images for ${themePreviewModal.themeName}`}
        >
          <div
            className="w-full max-w-[19rem] rounded-xl border border-border-color bg-[#0b1220] p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="truncate text-sm font-semibold text-zinc-100">{themePreviewModal.themeName}</p>
              <button
                type="button"
                onClick={closeThemePreviewModal}
                className="rounded-md p-1 text-zinc-300 transition hover:bg-white/10 hover:text-white"
                aria-label="Close theme preview"
              >
                <CancelIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
            {currentThemePreviewImage ? (
              <div className="relative">
                <img
                  src={currentThemePreviewImage}
                  alt={`${themePreviewModal.themeName} preview`}
                  className="h-[18rem] w-full rounded-lg object-contain bg-black/35"
                />
                {themePreviewModal.images.length > 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => navigateThemePreview(-1)}
                      className="absolute left-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border-color/70 bg-black/45 text-zinc-100 transition hover:bg-black/65"
                      aria-label="Previous preview image"
                    >
                      <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateThemePreview(1)}
                      className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border-color/70 bg-black/45 text-zinc-100 transition hover:bg-black/65"
                      aria-label="Next preview image"
                    >
                      <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <div className="flex h-[18rem] items-center justify-center rounded-lg border border-border-color/70 bg-black/35 text-sm text-zinc-500">
                No preview available
              </div>
            )}
            {themePreviewModal.images.length > 1 ? (
              <div className="mt-2 flex items-center justify-center">
                <span className="text-xs text-zinc-400">
                  {themePreviewModal.index + 1} / {themePreviewModal.images.length}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {failedSnapshot && (
        <div className="mb-2 shrink-0 rounded-lg border border-red-500/30 bg-red-900/30 p-3 text-lg">
          Previous request failed. You can edit and retry.
        </div>
      )}

      <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col">
        <div className="shrink-0 pb-1.5 pt-0">
          <header className="flex min-w-0 items-center gap-0">
            <button
              type="button"
              onClick={() => navigate("/whitelabel")}
              className="group inline-flex shrink-0 items-center justify-center rounded-none bg-transparent py-2 pl-1 pr-0.5 text-zinc-100 transition-colors hover:bg-white/10 hover:text-white active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              aria-label="Back to whitelabels"
              title="Back"
            >
              <ArrowBackIcon
                className="block transition-transform group-hover:-translate-x-0.5"
                sx={{ fontSize: 22, color: "currentColor" }}
              />
            </button>
            <h1
              className="m-0 min-w-0 flex-1 self-center text-lg font-semibold uppercase leading-tight tracking-wide sm:text-xl"
              style={{ color: STEPPER_PRIMARY }}
            >
              {createdWhitelabelId.trim() ||
              (forceEditRequested && Boolean(editWhitelabelId.trim()))
                ? "Edit"
                : "Create your brand"}
            </h1>
            {supportTicketId ? (
              <button
                type="button"
                className="inline-flex shrink-0 items-center justify-center rounded-md p-1.5 text-white transition-colors hover:bg-white/10"
                onClick={() => setSupportChatOpen(true)}
                aria-label="Open customer support chat"
                title="Customer support"
              >
                <img
                  src={SupportIcon}
                  alt=""
                  className="h-6 w-6"
                  style={{
                    filter:
                      "invert(100%) sepia(100%) saturate(0%) hue-rotate(139deg) brightness(110%) contrast(101%)",
                  }}
                />
              </button>
            ) : null}
          </header>

          <div className="py-1 sm:py-1.5">
          <div className="flex w-full flex-row items-start" data-wl-stepper="true">
            {STEPPER_STEPS.map((s, idx) => (
              <div key={s.key} className="flex min-w-0 flex-1 flex-col items-center">
                <div className="flex w-full items-center">
                  {idx === 0 ? (
                    <div className="min-h-1 flex-1" aria-hidden />
                  ) : (
                    <div
                      className="h-1 min-h-1 flex-1 rounded-l-full transition-[background-color] duration-200 ease-out"
                      style={{
                        backgroundColor:
                          stepperVisualIdx >= idx ? STEPPER_PRIMARY : STEPPER_LINE_GRAY,
                      }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => goToStep(idx)}
                    className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 transition-[box-shadow,background-color,border-color] duration-200 ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-color)]"
                    style={
                      stepperVisualIdx === idx
                        ? {
                            backgroundColor: STEPPER_PRIMARY,
                            borderColor: STEPPER_PRIMARY,
                            boxShadow:
                              "0 4px 16px color-mix(in srgb, var(--border-color) 42%, transparent)",
                          }
                        : {
                            backgroundColor: STEPPER_INACTIVE_FILL,
                            borderColor: STEPPER_INACTIVE_BORDER,
                          }
                    }
                    aria-current={stepperVisualIdx === idx ? "step" : undefined}
                    aria-label={`${s.label} step`}
                  >
                    {wlStepperIcon(s.key)}
                  </button>
                  {idx === STEPPER_STEPS.length - 1 ? (
                    <div className="min-h-1 flex-1" aria-hidden />
                  ) : (
                    <div
                      className="h-1 min-h-1 flex-1 rounded-r-full transition-[background-color] duration-200 ease-out"
                      style={{
                        backgroundColor:
                          stepperVisualIdx > idx ? STEPPER_PRIMARY : STEPPER_LINE_GRAY,
                      }}
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => goToStep(idx)}
                  className={`mt-2 w-full px-0.5 text-center text-[11px] leading-tight transition-colors duration-200 sm:text-sm ${
                    stepperVisualIdx === idx ? "font-bold" : "font-medium text-zinc-400"
                  }`}
                  style={stepperVisualIdx === idx ? { color: STEPPER_PRIMARY } : undefined}
                >
                  {s.label}
                </button>
              </div>
            ))}
          </div>
          </div>
        </div>

        <div
          ref={mainScrollRef}
          className="min-h-0 flex-1 overflow-x-visible overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]"
        >
        <div
          className="w-full pt-1"
          style={{ paddingBottom: `${scrollBottomSafePx}px` }}
        >
          <div className="overflow-x-visible overflow-y-visible rounded-xl border border-border-color bg-inputbox-color">
            <div key={stepperVisualIdx} className={stepPanelAnimClass}>
            {stepperVisualIdx === 1 && (
              <div className="p-3">
                <div>
                  <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-fuchsia-500/90">
                      <PaletteRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Look &amp; theme <span className="text-red-500">*</span>
                    </span>
                  </p>
                  <p className="text-xs text-zinc-400 mt-1">
                    This is your site&apos;s first impression — choose one look that fits your brand.
                  </p>
                </div>

                <SkinPickerCarousel
                  skins={skins}
                  selectedSkinId={selectedSkinId}
                  onSelectSkin={handleSelectSkin}
                />

                <div className="mt-3 border-t border-border-color/60 pt-3">
                  <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-violet-500/90 text-[11px] font-bold text-white">
                      T
                    </span>
                    <span>
                      Brand theme
                      {themeSelectionRequired ? <span className="text-red-500"> *</span> : null}
                    </span>
                  </p>
                  {themeConfigs.length > 0 ? (
                    <>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {themeConfigs.map((cfg) => {
                          const active = cfg.category === activeThemeBrowseCategory;
                          return (
                            <button
                              key={cfg.category}
                              type="button"
                              onClick={() => setActiveThemeBrowseCategory(cfg.category)}
                              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                                active
                                  ? "border-[var(--border-color)] bg-[color-mix(in_srgb,var(--border-color)_28%,transparent)] text-white"
                                  : "border-border-color bg-inputbox-color/30 text-zinc-300 hover:border-zinc-500"
                              }`}
                            >
                              {cfg.category}
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {(activeThemeCategory?.themes ?? []).map((theme) => {
                          const isSelected =
                            selectedThemeCategory === activeThemeCategory?.category &&
                            selectedThemeName === theme.name;
                          const [pr, pg, pb] = hexToRgbParts(theme.primary);
                          const [sr, sg, sb] = hexToRgbParts(theme.secondary);
                          const [br, bg, bb] = hexToRgbParts(theme.background);
                          return (
                            <button
                              key={`${activeThemeCategory?.category}-${theme.name}`}
                              type="button"
                              onClick={() => selectThemeCard(activeThemeCategory!.category, theme)}
                              className={`relative flex min-h-[3.15rem] w-full items-center justify-between overflow-hidden rounded-xl border px-0 py-0 text-left transition ${
                                isSelected
                                  ? "border-[var(--border-color)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--border-color)_55%,transparent),0_10px_24px_rgba(0,0,0,0.35)]"
                                  : "border-border-color/60 hover:border-zinc-500"
                              }`}
                              style={{
                                background:
                                  `linear-gradient(108deg, rgba(${br},${bg},${bb},0.6) 0%, rgba(${pr},${pg},${pb},0.18) 54%, rgba(${sr},${sg},${sb},0.2) 100%)`,
                              }}
                            >
                              <div className="absolute bottom-0 left-0 top-0 flex w-[8px] flex-col">
                                <div className="flex-1" style={{ backgroundColor: theme.primary }} />
                                <div className="flex-1" style={{ backgroundColor: theme.secondary }} />
                                <div className="flex-1" style={{ backgroundColor: theme.background }} />
                              </div>
                              <div className="flex min-w-0 flex-1 items-center pl-5 pr-14">
                                <div className="min-w-0">
                                  <p className="whitespace-normal break-words text-sm font-semibold leading-tight text-zinc-100">
                                    {theme.name}
                                  </p>
                                </div>
                              </div>
                              <div className="absolute right-1 top-1 z-10">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openThemePreviewModal(theme.name, theme.imageUrls);
                                  }}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-transparent p-0 text-zinc-200 transition hover:bg-white/10 hover:text-white"
                                  aria-label={`Preview theme screens for ${theme.name}`}
                                  title="Preview theme screens"
                                >
                                  <VisibilityRoundedIcon sx={{ fontSize: 14 }} />
                                </button>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="mt-2 rounded-lg border border-border-color/60 bg-inputbox-color/40 p-2.5 text-xs text-zinc-400">
                      Theme presets are not available for this skin right now.
                    </div>
                  )}
                </div>

                <div className="mt-3 border-t border-border-color/60 pt-3">
                  <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-sky-500/90">
                      <Inventory2RoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Products <span className="text-red-500">*</span>
                    </span>
                  </p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Tick what you want live on your site — at least one.
                  </p>

                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                    {categories.map((cat) => {
                      const selected = selectedCategories.includes(cat.key);
                      return (
                        <label
                          key={cat.key}
                          className="flex min-w-0 items-center gap-2.5 text-sm text-zinc-200 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleCategory(cat.key)}
                            className="h-4.5 w-4.5 accent-border-color cursor-pointer shrink-0"
                          />
                          <span className="min-w-0 leading-snug">{cat.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  {categoryTouched && selectedCategories.length === 0 && (
                    <p className="mt-2 text-xs text-red-400">
                      Pick at least one product to continue.
                    </p>
                  )}
                </div>

                <div className="mt-3 border-t border-border-color/60 pt-3">
                  <div className="rounded-2xl border border-[color-mix(in_srgb,var(--border-color)_45%,#1f3444)] bg-[color-mix(in_srgb,var(--border-color)_9%,#081722)] p-3">
                    <div className="text-center">
                      <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-100">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-amber-500/90">
                          <CreditCardRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                        </span>
                        <span>
                          Payment Modes <span className="text-red-500">*</span>
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-zinc-400">
                        Select the payment options players can use.
                      </p>
                    </div>

                    <div className="mt-3 space-y-2" role="group" aria-label="Payment modes">
                      <label
                        className={`group flex cursor-pointer items-center gap-2.5 rounded-xl border px-2.5 py-2 transition-colors ${
                          enableCryptoTransactions
                            ? "border-[color-mix(in_srgb,var(--border-color)_70%,#22d3ee)] bg-[color-mix(in_srgb,var(--border-color)_12%,#0b2230)]"
                            : "border-border-color/45 bg-inputbox-color/35 hover:border-border-color/75"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={enableCryptoTransactions}
                          onChange={() => toggleTransactionType("CRYPTO")}
                          className="h-4 w-4 shrink-0 accent-border-color cursor-pointer"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold leading-tight text-zinc-100">Crypto</span>
                          <span className="block text-xs text-zinc-400/90">Accept crypto deposits</span>
                        </span>
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ${
                            enableCryptoTransactions
                              ? "border-cyan-300/60 bg-gradient-to-br from-cyan-300/20 to-emerald-300/20 text-cyan-200"
                              : "border-zinc-600/70 bg-zinc-900/25 text-zinc-500"
                          }`}
                          aria-hidden
                        >
                          <CurrencyBitcoinRoundedIcon sx={{ fontSize: 16 }} />
                        </span>
                      </label>

                      <div
                        className={`rounded-xl border transition-colors ${
                          enableFiatTransactions
                            ? "border-[color-mix(in_srgb,var(--border-color)_70%,#22d3ee)] bg-[color-mix(in_srgb,var(--border-color)_12%,#0b2230)]"
                            : "border-border-color/45 bg-inputbox-color/35"
                        }`}
                      >
                        <label className="group flex cursor-pointer items-center gap-2.5 px-2.5 py-2">
                          <input
                            type="checkbox"
                            checked={enableFiatTransactions}
                            onChange={() => toggleTransactionType("FIAT")}
                            className="h-4 w-4 shrink-0 accent-border-color cursor-pointer"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold leading-tight text-zinc-100">Fiat</span>
                            <span className="block text-xs text-zinc-400/90">Accept bank / UPI payouts</span>
                          </span>
                          <span
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ${
                              enableFiatTransactions
                                ? "border-cyan-300/60 bg-gradient-to-br from-cyan-300/20 to-sky-300/20 text-cyan-200"
                                : "border-zinc-600/70 bg-zinc-900/25 text-zinc-500"
                            }`}
                            aria-hidden
                          >
                            <AccountBalanceRoundedIcon sx={{ fontSize: 15 }} />
                          </span>
                        </label>

                        <div
                          className={`border-t px-3 pb-2 pt-2 transition-opacity ${
                            enableFiatTransactions
                              ? "border-[color-mix(in_srgb,var(--border-color)_24%,#2a3f52)] opacity-100"
                              : "border-zinc-700/50 opacity-50"
                          }`}
                        >
                          <p className="text-xs font-medium text-zinc-300">Payout setup:</p>
                          <div className="mt-1.5 space-y-1.5" role="radiogroup" aria-label="Fiat payout setup">
                            <label
                              className={`flex items-center gap-2 text-sm ${
                                enableFiatTransactions
                                  ? "cursor-pointer text-zinc-200"
                                  : "cursor-not-allowed text-zinc-400"
                              }`}
                            >
                              <input
                                type="radio"
                                name="fiatBankSource"
                                value="own"
                                checked={fiatBankSource === "own"}
                                onChange={() => setFiatBankSource("own")}
                                disabled={!enableFiatTransactions}
                                className="h-3.5 w-3.5 accent-border-color shrink-0 disabled:cursor-not-allowed"
                              />
                              <span className="leading-snug">Use my own bank accounts</span>
                            </label>
                            <label
                              className={`flex items-center gap-2 text-sm ${
                                enableFiatTransactions
                                  ? "cursor-pointer text-zinc-200"
                                  : "cursor-not-allowed text-zinc-400"
                              }`}
                            >
                              <input
                                type="radio"
                                name="fiatBankSource"
                                value="provided"
                                checked={fiatBankSource === "provided"}
                                onChange={() => setFiatBankSource("provided")}
                                disabled={!enableFiatTransactions}
                                className="h-3.5 w-3.5 accent-border-color shrink-0 disabled:cursor-not-allowed"
                              />
                              <span className="leading-snug">Use platform-managed bank accounts</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {!enableFiatTransactions && !enableCryptoTransactions ? (
                      <p className="mt-2 text-xs text-red-400">
                        At least one payment mode is required.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className={`w-1/2 ${BTN_SECONDARY_CLASS}`}
                    onClick={handlePrev}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className={`w-1/2 ${BTN_PRIMARY_CLASS}`}
                    onClick={handleNext}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {stepperVisualIdx === 0 && (
              <div className="p-3">
                <div>
                  <label className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-orange-500/90">
                      <SellRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Brand name <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>
                <input
                  className="mt-1 p-2.5 w-full text-sm text-white bg-inputbox-color rounded-lg border border-border-color"
                  value={whitelabelName}
                  minLength={WL_MIN_BRAND_SUB_CHARS}
                  onChange={(e) => {
                    const rawName = e.target.value;
                    const sanitizedName = sanitizeAlphaNumeric(rawName);
                    const hasInvalidChars =
                      rawName.trim().length > 0 && rawName !== sanitizedName;
                    setWhitelabelName(rawName);
                    setNameHasInvalidChars(hasInvalidChars);
                    setNameAvailable(null);
                    setNameAvailabilityError("");
                    lastCheckedNameRef.current = null;
                    triggerNameAvailabilityCheck(rawName, hasInvalidChars);
                    if (!isSubdomainManuallyEdited) {
                      const autoSubdomain = sanitizedName.toLowerCase();
                      setSubdomain(autoSubdomain);
                      setSubdomainHasInvalidChars(false);
                      setDomainUrlAvailable(null);
                      setDomainAvailabilityError("");
                      triggerSubdomainAvailabilityCheck(autoSubdomain, false);
                    }
                  }}
                  placeholder="e.g. BetZ, WinSpin"
                />
                {whitelabelName.trim().length > 0 ? (
                  <div className="mt-2 text-xs leading-snug" aria-live="polite">
                    {whitelabelName.trim().length < WL_MIN_BRAND_SUB_CHARS ? (
                      <p className="text-amber-200/90">
                        Add at least {WL_MIN_BRAND_SUB_CHARS} characters.
                      </p>
                    ) : nameHasInvalidChars ? (
                      <p className="text-amber-300/95">Letters and numbers only — spaces and symbols won&apos;t work.</p>
                    ) : nameCheckQueued || nameChecking ? (
                      <p className="text-zinc-400">Checking this name…</p>
                    ) : nameAvailable === false ? (
                      <p className="text-red-400">Uh oh — that name&apos;s taken. Try another.</p>
                    ) : nameAvailable === true ? (
                      <p className="inline-flex items-center gap-1.5 text-emerald-400/95">
                        <CheckCircleRoundedIcon
                          className="text-emerald-400"
                          style={{ fontSize: 18 }}
                          aria-hidden
                        />
                        <span>That name is free — you&apos;re good.</span>
                      </p>
                    ) : nameAvailabilityError ? (
                      <p className="text-amber-200/85">{nameAvailabilityError}</p>
                    ) : nameAvailable == null ? (
                      <p className="text-zinc-400">
                        We&apos;ll check this name in a moment...
                      </p>
                    ) : (
                      <p className="text-amber-200/85">We couldn&apos;t verify this name. Try again.</p>
                    )}
                  </div>
                ) : null}

                <div className="mt-2">
                  <label
                    className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200"
                    htmlFor="wl-subdomain-input"
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-emerald-500/90">
                      <PublicRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Select your web address <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <div
                  className={[
                    "mt-1.5 flex min-w-0 flex-nowrap items-center gap-y-1 overflow-x-auto rounded-lg border bg-inputbox-color transition-[box-shadow,border-color] duration-200",
                    "focus-within:border-[color-mix(in_srgb,var(--border-color)_45%,var(--border-color))]",
                    "focus-within:shadow-[0_0_0_2px_color-mix(in_srgb,var(--border-color)_35%,transparent)]",
                    "border-border-color",
                  ].join(" ")}
                >
                  <span
                    className="select-none pl-2.5 font-mono text-xs text-zinc-500 sm:pl-3 sm:text-sm"
                    aria-hidden
                  >
                    https://
                  </span>
                  <input
                    id="wl-subdomain-input"
                    className="min-w-[6rem] flex-1 border-0 bg-transparent py-2.5 pl-1 pr-1 text-sm text-white outline-none focus:ring-0 sm:min-w-[8rem]"
                    value={subdomain}
                    minLength={WL_MIN_BRAND_SUB_CHARS}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const sanitizedLower = sanitizeAlphaNumeric(raw).toLowerCase();
                      const hasInvalidChars =
                        raw.trim().length > 0 && sanitizedLower !== raw.trim();
                      setIsSubdomainManuallyEdited(true);
                      setSubdomainHasInvalidChars(hasInvalidChars);
                      setSubdomain(raw);
                      setDomainUrlAvailable(null);
                      setDomainAvailabilityError("");
                      lastCheckedSubRef.current = null;
                      triggerSubdomainAvailabilityCheck(raw, hasInvalidChars);
                    }}
                    placeholder="betz"
                    autoComplete="off"
                    spellCheck={false}
                    aria-describedby="wl-subdomain-status"
                  />
                  {availableDomainSuffixes.length > 1 ? (
                    <select
                      className="mr-2.5 shrink-0 rounded border-0 bg-inputbox-color px-2 py-1 text-xs text-zinc-200 outline-none sm:mr-3 sm:text-sm"
                      value={selectedDomainSuffix}
                      onChange={(e) => setSelectedDomainSuffix(normalizeDomainSuffix(e.target.value))}
                      aria-label="Select web address domain"
                    >
                      {availableDomainSuffixes.map((domain) => (
                        <option key={domain} value={domain}>
                          .{domain}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className="select-none pr-2.5 font-mono text-xs text-zinc-500 sm:pr-3 sm:text-sm"
                      aria-hidden
                    >
                      .{normalizeDomainSuffix(selectedDomainSuffix) || "whitelabel.com"}
                    </span>
                  )}
                </div>
                <div
                  id="wl-subdomain-status"
                  role="status"
                  aria-live="polite"
                  className="mt-1 min-h-[1.25rem] text-xs leading-snug transition-all duration-200 ease-out"
                >
                  {domainStatusPhase === "invalid" ? (
                    <p className="text-amber-300/95 transition-opacity duration-200">
                      Lowercase letters and numbers only — no spaces or symbols (this becomes your
                      URL).
                    </p>
                  ) : domainStatusPhase === "short" ? (
                    <p className="text-zinc-500 transition-opacity duration-200">
                      {WL_MIN_BRAND_SUB_CHARS - wlSubTrim.length} more character
                      {WL_MIN_BRAND_SUB_CHARS - wlSubTrim.length === 1 ? "" : "s"} needed before we
                      check this address.
                    </p>
                  ) : domainStatusPhase === "checking" ? (
                    <p className="flex items-center gap-2 text-zinc-400 transition-opacity duration-200">
                      <span
                        className="inline-block size-3.5 shrink-0 animate-spin rounded-full border-2 border-zinc-500 border-t-transparent"
                        aria-hidden
                      />
                      Checking availability...
                    </p>
                  ) : domainStatusPhase === "taken" ? (
                    <p className="flex items-center gap-1.5 text-red-400/95 transition-opacity duration-200">
                      <span aria-hidden>!</span>
                      Already taken. Try another.
                    </p>
                  ) : domainStatusPhase === "available" ? (
                    <p className="inline-flex items-center gap-1.5 font-medium text-emerald-400/95 transition-opacity duration-200">
                      <CheckCircleRoundedIcon sx={{ fontSize: 18 }} className="shrink-0" aria-hidden />
                      Available.
                    </p>
                  ) : domainStatusPhase === "unknown" ? (
                    <p className="flex items-center gap-1.5 text-zinc-400 transition-opacity duration-200">
                      {domainAvailabilityError ? (
                        <>
                          <span aria-hidden>!</span>
                          {domainAvailabilityError}
                        </>
                      ) : (
                        <>
                          <span
                            className="inline-block size-3.5 shrink-0 animate-spin rounded-full border-2 border-zinc-500 border-t-transparent"
                            aria-hidden
                          />
                          We&apos;ll check this address in a moment...
                        </>
                      )}
                    </p>
                  ) : null}
                </div>
                </div>

                <div className="mt-3 rounded-xl border border-border-color/60 bg-[radial-gradient(120%_120%_at_50%_-20%,color-mix(in_srgb,var(--border-color)_20%,transparent),transparent)] p-3">
                  <p className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-100">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-indigo-500/90">
                      <CloudUploadRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>Brand assets</span>
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-400">Upload assets to personalize the site.</p>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-dashed border-[color-mix(in_srgb,var(--border-color)_45%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_10%,transparent)] p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <p className="text-sm font-semibold text-zinc-100">Logo</p>
                          <span className="inline-flex rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-zinc-300">
                            PNG · Max 1MB
                          </span>
                          <span className="text-[10px] font-medium text-zinc-400">Recommended 200KB</span>
                        </div>
                        <AutoAwesomeRoundedIcon
                          sx={{ fontSize: 18, color: "color-mix(in_srgb,var(--border-color)_70%,white)" }}
                        />
                      </div>
                      <p className="mt-1 text-[11px] text-zinc-400">
                        AI Generate can be used up to 3 times.
                      </p>
                      <input
                        ref={logoFileInputRef}
                        id="whitelabel-logo-upload"
                        className="hidden"
                        type="file"
                        accept="image/png"
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          if (!selectedFile) {
                            setLogo(null);
                            e.currentTarget.value = "";
                            notifyPickerDismissedWithoutFile();
                            return;
                          }
                          if (selectedFile.type !== "image/png") {
                            toast.error("Only PNG logo is allowed.");
                            e.currentTarget.value = "";
                            return;
                          }
                          if (selectedFile.size > MAX_LOGO_UPLOAD_BYTES) {
                            toast.error("Logo must be 1MB or smaller.");
                            e.currentTarget.value = "";
                            return;
                          }
                          setLogo(selectedFile);
                          setGeneratedLogoBase64("");
                          setGeneratedLogoMime("");
                          setGeneratedLogoPreviewUrl("");
                        }}
                      />
                      <div className="mt-2 grid grid-cols-[auto_auto] items-start gap-x-2 gap-y-1">
                        <label
                          htmlFor="whitelabel-logo-upload"
                          className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-[color-mix(in_srgb,var(--border-color)_50%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_18%,transparent)] px-3 text-xs font-semibold text-zinc-100 hover:bg-[color-mix(in_srgb,var(--border-color)_28%,transparent)]"
                        >
                          <CloudUploadRoundedIcon sx={{ fontSize: 16 }} />
                          Upload
                        </label>
                        <button
                          type="button"
                          onClick={handleGenerateLogo}
                          disabled={
                            isGeneratingLogo ||
                            nameAvailable !== true ||
                            generateLogoAttemptCount >= MAX_GENERATED_LOGO_ATTEMPTS
                          }
                          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[color-mix(in_srgb,var(--border-color)_60%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_24%,transparent)] px-3 text-xs font-semibold text-zinc-100 hover:bg-[color-mix(in_srgb,var(--border-color)_34%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
                          title={
                            generateLogoAttemptCount >= MAX_GENERATED_LOGO_ATTEMPTS
                              ? "Logo generation limit reached (max 3 attempts)"
                              : nameAvailable === true
                                ? "Generate logo"
                                : "Brand name must be available to generate"
                          }
                        >
                          <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />
                          {isGeneratingLogo ? "Generating..." : "Generate"}
                        </button>
                        <span
                          className={`col-start-2 inline-flex justify-self-start rounded-full px-2 py-0.5 text-[10px] font-medium leading-none ${
                            generateLogoAttemptCount >= MAX_GENERATED_LOGO_ATTEMPTS
                              ? "bg-rose-500/15 text-rose-200"
                              : "bg-white/10 text-zinc-300"
                          }`}
                        >
                          {Math.max(0, MAX_GENERATED_LOGO_ATTEMPTS - generateLogoAttemptCount)} attempts left
                        </span>
                      </div>
                      {(logoPreviewUrl || generatedLogoPreviewUrl) && (
                        <div className="mt-3 rounded-lg border border-border-color/60 bg-inputbox-color p-2">
                          <div className="relative">
                            <img
                              src={logoPreviewUrl || generatedLogoPreviewUrl}
                              alt="Logo preview"
                              className={`h-16 w-full rounded object-contain ${
                                generatedLogoPreviewUrl && !logoPreviewUrl ? "bg-white" : ""
                              }`}
                            />
                            <button
                              type="button"
                              aria-label="Remove logo"
                              className="absolute -right-1.5 -top-1.5 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--border-color)] text-[14px] font-bold leading-none text-white shadow-[0_6px_18px_rgba(0,0,0,0.35)] transition-transform duration-150 hover:scale-105 hover:opacity-95"
                              onClick={() => {
                                setLogo(null);
                                setGeneratedLogoBase64("");
                                setGeneratedLogoMime("");
                                setGeneratedLogoPreviewUrl("");
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-dashed border-[color-mix(in_srgb,var(--border-color)_38%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_8%,transparent)] p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-zinc-100">GIF</p>
                          <span className="inline-flex rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-zinc-300">
                            GIF · Max 500KB
                          </span>
                        </div>
                        <GifBoxRoundedIcon
                          sx={{ fontSize: 18, color: "color-mix(in_srgb,var(--border-color)_65%,white)" }}
                        />
                      </div>
                      <p className="mt-1 text-[11px] text-zinc-400">Optional</p>
                      <input
                        ref={gifFileInputRef}
                        id="whitelabel-gif-upload"
                        className="hidden"
                        type="file"
                        accept="image/gif"
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          if (!selectedFile) {
                            setGif(null);
                            e.currentTarget.value = "";
                            notifyPickerDismissedWithoutFile();
                            return;
                          }
                          if (selectedFile.type !== "image/gif") {
                            toast.error("Only GIF file is allowed.");
                            e.currentTarget.value = "";
                            return;
                          }
                          if (selectedFile.size > MAX_GIF_UPLOAD_BYTES) {
                            toast.error("GIF must be 500KB or smaller.");
                            e.currentTarget.value = "";
                            return;
                          }
                          setDraftLoaderBase64("");
                          setDraftLoaderMime("");
                          setDraftLoaderPreviewUrl("");
                          setGif(selectedFile);
                        }}
                      />
                      <label
                        htmlFor="whitelabel-gif-upload"
                        className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[color-mix(in_srgb,var(--border-color)_45%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_18%,transparent)] px-3 py-1.5 text-xs font-semibold text-zinc-100 hover:bg-[color-mix(in_srgb,var(--border-color)_28%,transparent)]"
                      >
                        <CloudUploadRoundedIcon sx={{ fontSize: 16 }} />
                        Upload
                      </label>
                      {(gifPreviewUrl || draftLoaderPreviewUrl) && (
                        <div className="mt-3 rounded-lg border border-border-color/60 bg-inputbox-color p-2">
                          <div className="relative">
                            <img
                              src={gifPreviewUrl || draftLoaderPreviewUrl}
                              alt="GIF preview"
                              className="h-16 w-full rounded object-contain"
                            />
                            <button
                              type="button"
                              aria-label="Remove GIF"
                              className="absolute -right-1.5 -top-1.5 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--border-color)] text-[14px] font-bold leading-none text-white shadow-[0_6px_18px_rgba(0,0,0,0.35)] transition-transform duration-150 hover:scale-105 hover:opacity-95"
                              onClick={() => {
                                setGif(null);
                                setDraftLoaderBase64("");
                                setDraftLoaderMime("");
                                setDraftLoaderPreviewUrl("");
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    className={`min-w-[120px] ${BTN_PRIMARY_CLASS}`}
                    onClick={handleNext}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {stepperVisualIdx === 2 && (
              <div className="p-3">
                {renderWlPackagesPicker()}

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className={`w-1/2 ${BTN_SECONDARY_CLASS}`}
                    onClick={handlePrev}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className={`w-1/2 ${BTN_PRIMARY_CLASS}`}
                    onClick={handleNext}
                    disabled={!packagesStepCanContinue}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {stepperVisualIdx === 3 && (
              <div className="overflow-visible p-3">
                <div className="mt-2 space-y-2.5">
                  {[
                    {
                      key: "brand",
                      label: "Brand name",
                      value: whitelabelName.trim() || "—",
                      icon: <SellRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-orange-500/90",
                      missingIds: ["wl-name", "wl-name-len", "wl-name-bad", "wl-name-check"],
                    },
                    {
                      key: "logo",
                      label: "Logo",
                      value: logoComplete ? "Uploaded" : "Not uploaded",
                      trailingNode: logoComplete ? (
                        <div className="h-16 w-16 overflow-hidden rounded-md border border-white/15 bg-black/20">
                          <img
                            src={
                              logoPreviewUrl ||
                              generatedLogoPreviewUrl ||
                              (generatedLogoBase64
                                ? `data:${generatedLogoMime || "image/png"};base64,${generatedLogoBase64}`
                                : "")
                            }
                            alt="Selected logo"
                            className="h-full w-full object-contain object-center"
                          />
                        </div>
                      ) : null,
                      icon: <CloudUploadRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-cyan-500/90",
                      missingIds: ["logo"],
                    },
                    {
                      key: "web",
                      label: "Web address",
                      value: reviewWebAddressSummary,
                      icon: <PublicRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-emerald-500/90",
                      missingIds: ["sub", "sub-chars", "sub-len", "sub-bad", "sub-check"],
                    },
                    {
                      key: "skin",
                      label: themeSelectionRequired ? "Skin / Theme" : "Skin",
                      value: themeSelectionRequired
                        ? `${reviewSkinSummary} - ${reviewThemeSummary}`
                        : reviewSkinSummary,
                      icon: <PaletteRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-fuchsia-500/90",
                      missingIds: ["skin"],
                    },
                    {
                      key: "products",
                      label: "Products",
                      value: reviewCategoriesSummary,
                      icon: <Inventory2RoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-sky-500/90",
                      missingIds: ["categories"],
                    },
                    {
                      key: "payments",
                      label: "Payments",
                      value: reviewTransactionTypesSummary,
                      icon: <CreditCardRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-amber-500/90",
                      missingIds: ["tx", "fiat-src"],
                    },
                    {
                      key: "plan",
                      label: "Plan",
                      value: selectedWlPackageLabel,
                      icon: <VerifiedRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                      chipClass: "bg-violet-600/90",
                      missingIds: ["pkg-load", "pkg-none", "pkg-pick"],
                    },
                  ].map((item) => {
                    const fixTarget = missingMandatoryItems.find((m) =>
                      item.missingIds.includes(m.id)
                    );
                    const addActionIds = new Set([
                      "skin",
                      "categories",
                      "wl-name",
                      "sub",
                      "logo",
                      "tx",
                      "fiat-src",
                      "pkg-pick",
                      "pkg-none",
                      "pkg-load",
                    ]);
                    const fixActionText = fixTarget
                      ? addActionIds.has(fixTarget.id)
                        ? "Add"
                        : "Change"
                      : "";
                    return (
                      <div
                        key={item.key}
                        className="flex items-center gap-2.5 rounded-xl border border-[color-mix(in_srgb,var(--border-color)_38%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_14%,#0b1220)] px-2.5 py-2.5 sm:px-3"
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${item.chipClass}`}
                        >
                          {item.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium tracking-wide text-[color-mix(in_srgb,var(--border-color)_76%,white)]">
                            {item.label}
                          </p>
                          <p
                            className={`text-sm font-medium leading-snug text-zinc-100 sm:text-base ${
                              item.key === "web" ? "break-all text-sm sm:text-base" : ""
                            }`}
                          >
                            {item.value}
                          </p>
                        </div>
                        {item.trailingNode ? <div className="shrink-0">{item.trailingNode}</div> : null}
                        {fixTarget ? (
                          <button
                            type="button"
                            className="shrink-0 rounded-md border border-[color-mix(in_srgb,var(--border-color)_45%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_16%,transparent)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[color-mix(in_srgb,var(--border-color)_70%,white)] transition-colors hover:bg-[color-mix(in_srgb,var(--border-color)_24%,transparent)]"
                            onClick={() => goToStep(steps.indexOf(fixTarget.step))}
                          >
                            {fixActionText}
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div className="relative mt-3 w-full">
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      className={`w-1/2 shrink-0 ${BTN_SECONDARY_CLASS}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrev();
                      }}
                    >
                      Back
                    </button>
                    <div className="relative w-1/2 min-w-0">
                      <button
                        type="button"
                        className={`relative z-0 w-full ${BTN_PRIMARY_CLASS}`}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !allMandatoryFilled}
                      >
                        {isSubmitting ? "Sending…" : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {isGeneratingLogo ? (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/65 px-4"
          role="status"
          aria-live="polite"
          aria-label="Generating logo, please wait"
        >
          <div className="w-full max-w-xs rounded-xl border border-border-color bg-inputbox-color px-4 py-3 text-center shadow-[0_14px_38px_rgba(0,0,0,0.45)]">
            <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-zinc-400 border-t-transparent" />
            <p className="mt-3 text-sm font-semibold text-zinc-100">Generating logo...</p>
            <p className="mt-1 text-xs text-zinc-300">This will take a few seconds. Please wait.</p>
          </div>
        </div>
      ) : null}

      <WhitelabelSupportChatPopup
        isOpen={supportChatOpen}
        onClose={() => setSupportChatOpen(false)}
        ticketId={supportTicketId}
      />
    </div>
  );
};

export default CreateWhitelabel;
